module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('title', [_vm._v("Canonical Theology")]), _vm._v(" "), _c('link', {
    attrs: {
      "href": "../styles/stylesheet.css",
      "rel": "stylesheet",
      "type": "text/css"
    }
  }), _vm._v(" "), _c('p', {
    staticClass: "CN",
    attrs: {
      "id": "ch002"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_16"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch002"
    }
  }, [_vm._v("CHAPTER 2")])]), _vm._v(" "), _c('p', {
    staticClass: "CT"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch002"
    }
  }, [_vm._v("Defining the Intrinsic Canon")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush"
  }, [_vm._v("There is considerable ongoing debate and confusion regarding just how “canon” should be defined and understood, what the nature of the canon is and what it includes, and whether and to what extent the biblical canon is (or should be) authoritative. This chapter outlines a theological approach to the issue of canon by way of an explication of the intrinsic canon model, focusing on two primary issues: (1) competing definitions and understandings regarding the nature of the canon, particularly what or who makes the biblical canon “canonical” and determines its scope; and (2) the nature and scope of the biblical canon according to an intrinsic canon perspective.")]), _vm._v(" "), _c('p', {
    staticClass: "H1"
  }, [_vm._v("D"), _c('span', {
    staticClass: "small"
  }, [_vm._v("EFINING THE")]), _vm._v(" C"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ANON OF")]), _vm._v(" S"), _c('span', {
    staticClass: "small"
  }, [_vm._v("CRIPTURE")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("The canon debate hinges on the way that the term “canon” is defined and requires attention to what "), _c('i', [_vm._v("kind")]), _vm._v(" of definition is in view, whether regarding the canon’s nature (what "), _c('i', [_vm._v("is")]), _vm._v(" the canon?) or its historical function (when did the canon function as such?). Perspectives on the nature of the canon are split between those who define the canon as a corpus of divinely determined books (the intrinsic canon) and those who view the canon as a collection of community-determined books (the community canon). Perspectives on the historical function of the canon are further divided regarding "), _c('i', [_vm._v("when")]), _vm._v(" the canon functions as canon and when the term “canon” ought to be applied."), _c('a', {
    attrs: {
      "id": "fn1"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn1"
    }
  }, [_vm._v("1")])]), _vm._v(" The predominant framework of the canon debate in recent times has been "), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }, [_vm._v("relative to this latter "), _c('i', [_vm._v("kind")]), _vm._v(" of definition, which some take as bearing on, or even determinative of, the former. For our purposes, however, we turn first to the canon’s nature.")])]), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }, [_c('p', {
    staticClass: "H2"
  }, [_vm._v("What Is the Nature of the Canon?")])]), _c('p', {
    staticClass: "Bodyflush1"
  }, [_c('a', {
    attrs: {
      "id": "page_17"
    }
  }, [_vm._v("In my view, the crucial theological question that cuts across the canon debate is: what makes the canon “canonical”? One might approach the canon as the result of historical phenomena, apart from weighing in regarding whether and to what extent God played a role in the canon’s history. Framed this way, the question is whether one considers the biblical canon “an extrinsic phenomenon” wherein “a later ecclesiastical development [was] imposed on books originally written for another purpose,” on the one hand, or as a notion that “developed early and naturally out of the Christian religion.”")]), _c('a', {
    attrs: {
      "id": "fn2"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn2"
    }
  }, [_vm._v("2")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Whereas I view the canon as a historical phenomenon, I am most interested in the particularly theological form of this question: Is the canon determined by humans or by God?"), _c('a', {
    attrs: {
      "id": "fn3"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn3"
    }
  }, [_vm._v("3")])]), _vm._v(" The intrinsic canon model and community canon model sharply diverge regarding precisely this question, with significant theological implications.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("In the community canon model, the community determines the canon. This tends toward reducing the definition of canon to a collection of books deemed authoritative (and thus made canonical) by a given community."), _c('a', {
    attrs: {
      "id": "fn4"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn4"
    }
  }, [_vm._v("4")])]), _vm._v(" As such, the canon might be viewed as an extension of community authority. "), _c('a', {
    attrs: {
      "id": "page_18"
    }
  }, [_vm._v("The community canon model is itself split in this regard between those who believe the canon was fixed by the church at some time in the past (the ecclesial fixed canon view) and those who favor a more fluid definition of the canon as shaped by contemporary community consensus (the adaptable canon view).")])]), _c('a', {
    attrs: {
      "id": "page_18"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_18"
    }
  }, [_vm._v("Within the community canon model’s broad scope, then, some suggest that “canon” is an anachronistic imposition on selected writings by external community forces.")]), _c('a', {
    attrs: {
      "id": "fn5"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn5"
    }
  }, [_vm._v("5")])]), _vm._v(" Others reduce the notion of canon to the decisions of a Christian community such that “biblical canons depend for content and order on the denomination or communion in view.”"), _c('a', {
    attrs: {
      "id": "fn6"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn6"
    }
  }, [_vm._v("6")])]), _vm._v(" Here, canon is “something officially or authoritatively imposed upon certain literature.” As such, it is “basically a community’s paradigm for how to continue the dialogue in ever changing socio-political contexts.”"), _c('a', {
    attrs: {
      "id": "fn7"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn7"
    }
  }, [_vm._v("7")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Others attribute less fluidity to the canon yet retain the centrality of the community in canon-determination. Paul McGlasson states, “"), _c('i', [_vm._v("Canon")]), _vm._v(" by definition refers to a sacred text treasured in an ongoing community of faith.”"), _c('a', {
    attrs: {
      "id": "fn8"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn8"
    }
  }, [_vm._v("8")])]), _vm._v(" Charles Scalise similarly points out, “Including a text in the canon means that it has theological importance for the communities who read it as Scripture.”"), _c('a', {
    attrs: {
      "id": "fn9"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn9"
    }
  }, [_vm._v("9")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_vm._v("Despite variations regarding fluidity and authority, then, the common denominator of community approaches is the location of canon-determination in the community such that the canon is defined as those books that some community makes authoritative (and thus “canonical”).")])]), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_vm._v("Conversely, according to the intrinsic canon model, divine action is the determining factor regarding the nature and scope of the canon. God determines the canon, which the community "), _c('i', [_vm._v("merely")]), _vm._v(" recognizes.")]), _c('a', {
    attrs: {
      "id": "fn10"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn10"
    }
  }, [_vm._v("10")])]), _vm._v(" Writings, then, are “canonical” insofar as God divinely commissioned them as the rule or standard of faith and practice. Whereas the community’s recognition of the canon holds crucial implications regarding its function within that community, community-recognition does not bear on canonicity "), _c('i', [_vm._v("qua")]), _vm._v(" canonicity. The divide, then, is not over "), _c('i', [_vm._v("whether")]), _vm._v(" the community plays a role with regard to the canon but precisely "), _c('i', [_vm._v("what")]), _vm._v(" that role is, particularly regarding what makes the canon “canonical” and by whose authority the canon is the “rule” or “standard.”")]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("Intrinsic Canonicity")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("I define the intrinsic canon as the corpus of writings commissioned by God to be the “rule” or “standard” of Christian faith and practice."), _c('a', {
    attrs: {
      "id": "fn11"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn11"
    }
  }, [_vm._v("11")])]), _vm._v(" Thus, the intrinsic canon refers to those writings that are "), _c('i', [_vm._v("intrinsically")]), _vm._v(" canonical by virtue of what the canon "), _c('i', [_vm._v("is")]), _vm._v(" as the result of divine action (i.e., via divine commission)."), _c('a', {
    attrs: {
      "id": "fn12"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn12"
    }
  }, [_vm._v("12")])]), _vm._v(" This "), _c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_vm._v("does not mean that the canonical writings result from exclusively divine action but that their intrinsically canonical nature is derived from the uniquely divine activity that, combined with human activity, resulted in the product of Scripture (cf. 2 Tim 3:16; 2 Pet 1:20-21).")]), _c('a', {
    attrs: {
      "id": "fn13"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn13"
    }
  }, [_vm._v("13")])]), _vm._v(" Hereafter, I will use “divine commission” as an umbrella term for the various divine activities in the production of the canonical writings."), _c('a', {
    attrs: {
      "id": "fn14"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn14"
    }
  }, [_vm._v("14")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("This intrinsic canon view rests on a minimal definition of the word “canon” ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("κανών")]), _vm._v(") in the sense of “rule” or “standard.”"), _c('a', {
    attrs: {
      "id": "fn15"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn15"
    }
  }, [_vm._v("15")])]), _vm._v(" Paul appears to use the term "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("κανών")]), _vm._v(" in this minimal sense (though not of the biblical canon) when he states, “as many as walk according to this rule ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("κανόνι")]), _vm._v("], peace and mercy be upon them” (Gal 6:16)."), _c('a', {
    attrs: {
      "id": "fn16"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn16"
    }
  }, [_vm._v("16")])]), _vm._v(" With this minimal definition of “canon,” the intrinsic canon of Scripture is those writings commissioned (in the broad sense) by God to be the “rule” or “standard” ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("κανών")]), _vm._v(") of faith and practice.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Given that God is the ruler of the universe, whatever God commissions "), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }, [_vm._v("as the rule or standard is thereby canonical. According to the words of Christ in Luke 11:49, God commissioned, or sent ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ἀποστέλλω")]), _vm._v("), “prophets and apostles” as his authoritative spokespersons and witnesses. Further, Eph 2:19-20 describes the “household” of God as “built on the foundation of the apostles and prophets, Christ Jesus Himself being the corner "), _c('i', [_vm._v("stone")]), _vm._v("” (cf. Eph 3:5; 4:11; 1 Cor 12:28-29; 2 Pet 3:2; Rev 18:20; translation following NASB).")])]), _c('a', {
    attrs: {
      "id": "page_21"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_21"
    }
  }, [_vm._v("Such divine commission of prophets and apostles closely relates to the covenantal context of Scripture. A strong case can be made that the canon is a covenant document, depicting and regulating the relationship between God and his people. In this context, many biblical writers describe their prophetic and/or apostolic activity as responsive to divine command such that they disseminate not merely their own thoughts but divine revelation written down under the guidance of inspiration. The nature and authority of such writings derives from divine commission.")]), _c('a', {
    attrs: {
      "id": "fn17"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn17"
    }
  }, [_vm._v("17")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Indeed, biblical writers repeatedly describe their teachings and/or those of others as theologically authoritative in virtue of their divine origin. For example, Paul contends: “All Scripture is inspired ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("θεόπνευστος")]), _vm._v("] by God and profitable for teaching, for reproof, for correction, for training in righteousness” (2 Tim 3:16). Further, 2 Pet 1:20-21 emphasizes the divine origin of Scripture, explaining “that no prophecy of Scripture is a matter of one’s own interpretation, for no prophecy was ever made by an act of human will, but men moved by the Holy Spirit spoke from God.” In these instances and others, the term “Scripture” is used in reference to writings originated through divine action and theologically authoritative in virtue of divine action (i.e., commission). As such, any writings that are properly “Scripture,” in this sense, are thereby “canonical” in the minimal sense defined above."), _c('a', {
    attrs: {
      "id": "fn18"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn18"
    }
  }, [_vm._v("18")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_22"
    }
  }, [_vm._v("Note, at this juncture, that the above texts are not being used to bear on "), _c('i', [_vm._v("which")]), _vm._v(" extant writings are “canonical” but as evidence of the claim that canonical writers believed that divine commission (broadly) invested their teachings (oral and written) with divine authority (cf. 1 Thess 2:13). If such claims are true, any such written (inscripturated) teachings would be properly “canonical” in the minimal sense defined above and would be so before and apart from community recognition (and, indeed, often set directly against community consensus; see chapter 3). Thus, whereas those who believe the canon is the product of external imposition often contend that biblical writers did not “conceive of themselves as producing authoritative texts,” biblical writers “do provide substantial indications that they understood their message as authoritative, and often do so quite plainly” (see the further evidence below).")]), _c('a', {
    attrs: {
      "id": "fn19"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn19"
    }
  }, [_vm._v("19")])])]), _vm._v(" "), _c('p', {
    staticClass: "H1"
  }, [_vm._v("R"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ECOGNIZING THE")]), _vm._v(" I"), _c('span', {
    staticClass: "small"
  }, [_vm._v("NTRINSIC")]), _vm._v(" C"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ANON")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("With the intrinsic canon model, then, God is the one who makes the canon canonical and, accordingly, determines (via divine commission) the contents of the canon. Among the community’s duties, then, is to properly recognize the divinely commissioned canon. Accordingly, the intrinsic canon approach looks for evidence of divine commission. The biblical data itself provides key evidence via internal claims to such divine commission, indicating some criteria that might aid in recognizing which writings were divinely commissioned as covenantally authoritative (i.e., canonical).")]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("The Canon as Christocentric Covenant Document")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("The contents of the intrinsic canon arise from within the context of God’s covenant relationship with his people wherein there is a “tight connection between the giving of covenants and the production of canonical texts.”"), _c('a', {
    attrs: {
      "id": "fn20"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn20"
    }
  }, [_vm._v("20")])]), _vm._v(" Following "), _c('a', {
    attrs: {
      "id": "page_23"
    }
  }, [_vm._v("the Exodus, the Torah depicts Moses as divinely commissioned to write down divinely authoritative teachings, prescribed as a covenantal rule or standard, to be preserved without alteration (Deut 4:2) and passed down to future generations (cf. Deut 6; 29:29; 31:10-13). As many scholars have recognized, Deuteronomy appears to share a similar structure with other ancient Near Eastern treaties, including (most significantly for our purposes) a “deposit of the written text of the covenant.”")]), _c('a', {
    attrs: {
      "id": "fn21"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn21"
    }
  }, [_vm._v("21")])]), _vm._v(" Further, the Ten Commandments themselves are proclaimed in the context of God’s redemptive activity: “I am the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(" your God, who brought you out of the land of Egypt, out of the house of slavery” (Exod 20:2). Here and elsewhere, the “Old Testament pattern” is “that canonical documents are distinctively the result of God’s redemptive activity on behalf of his people and function to proclaim that redemptive activity to his people (and to the nations).”"), _c('a', {
    attrs: {
      "id": "fn22"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn22"
    }
  }, [_vm._v("22")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Even as the OT canonical writings might thus be seen as depositing the written text of the covenantal relationship between God and his people, one might similarly view the NT as containing the Christ-commissioned canonical documentation of the new covenant, patterned after and building on the OT as covenant document. Indeed, “the concept of a written canon of Scripture is woven into the very covenantal fabric of both the Old Testament and the New Testament.”"), _c('a', {
    attrs: {
      "id": "fn23"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn23"
    }
  }, [_vm._v("23")])]), _vm._v(" The OT’s “archetypal redemptive event,” the Exodus, is followed by the giving of the Law in the context of the wider covenant and written documentation thereof, the command to preserve and proclaim such “canonical” teachings, and the post-Mosaic generations of prophets as divinely commissioned proclaimers thereof (e.g., 2 Kgs 17:13; see below)."), _c('a', {
    attrs: {
      "id": "fn24"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn24"
    }
  }, [_vm._v("24")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("A similar NT pattern emerges in that the Christ event, the redemptive event "), _c('i', [_vm._v("par excellence")]), _vm._v(", is framed by Christ within the new covenant (Luke 22:20; cf. 1 Cor 11:25; 2 Cor 3:6) that he mediates (Heb 9:15; 12:24; cf. 8:8, "), _c('a', {
    attrs: {
      "id": "page_24"
    }
  }, [_vm._v("13; Jer 31:31) alongside proclamation of the corresponding law of love upon which the “whole Law and the Prophets” depend (Matt 22:36-40).")]), _c('a', {
    attrs: {
      "id": "fn25"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn25"
    }
  }, [_vm._v("25")])]), _vm._v(" Further, Christ commissions the apostles to preserve, proclaim, and disseminate his acts, teachings, and commands, and make disciples of all nations (cf. Matt 28:19-20). Accordingly, the Christ-commissioned apostolic generation preserved in writing for future generations their unique witness to the unrepeatable Christ event, depositing a permanent Christ-commissioned (and thus “canonical”) testament."), _c('a', {
    attrs: {
      "id": "fn26"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn26"
    }
  }, [_vm._v("26")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("At the epicenter of covenant and canon, then, stands Christ. Many have recognized Christ as the center of the canon theologically; the OT points toward the Christ event and is fulfilled (without being nullified) therein (cf. Matt 5:17-18; Heb 1:1-2). However, Christ is also the center of the canon in that (broadly speaking) he appears to ratify the OT and commission the NT.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Christ repeatedly refers to the “Law and the Prophets” as authoritative (e.g., Matt 5:17; 7:12; 11:13; 22:40; Luke 16:16; cf. 16:29; Matt 11:13) and, in one particularly notable instance, states that “all things which are written about Me in the Law of Moses and the Prophets and the Psalms must be fulfilled” (Luke 24:44; cf. 24:27)."), _c('a', {
    attrs: {
      "id": "fn27"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn27"
    }
  }, [_vm._v("27")])]), _vm._v(" Some have suggested this may refer to the Law, Prophets, "), _c('a', {
    attrs: {
      "id": "page_25"
    }
  }, [_vm._v("and Writings sections of the Hebrew Bible, with “Psalms” perhaps designating the entire Writings section, of which it is the first book. Further, some point to Jesus’s statement in Luke 11:51, “from the blood of Abel to the blood of Zechariah, who was killed between the altar and the house "), _c('i', [_vm._v("of God")]), _vm._v("” as probably referring to the first and last martyrs in the Hebrew OT, thus encapsulating the Hebrew Bible from beginning to the end.")]), _c('a', {
    attrs: {
      "id": "fn28"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn28"
    }
  }, [_vm._v("28")])]), _vm._v(" While inconclusive by themselves, these passages, coupled with other evidence such as the extensive use of the OT by Jesus and the apostles"), _c('a', {
    attrs: {
      "id": "fn29"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn29"
    }
  }, [_vm._v("29")])]), _vm._v(" and other evidence regarding recognition of a functional OT canon by the first century (see chapter 3), suggest that Jesus and his apostles held an OT canon of Scripture (at least in the minimal sense of a corpus that they recognized as a divinely commissioned “rule” or “standard”), the boundaries of which they knew and expected their audience to know."), _c('a', {
    attrs: {
      "id": "fn30"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn30"
    }
  }, [_vm._v("30")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("Beyond the examples noted earlier (e.g., Luke 24:27, 44-45; 2 Tim 3:16; 2 Pet 1:20-21), Matthew writes, “all this has taken place to fulfill the Scriptures of the prophets” (Matt 26:56; cf. 1:22; 2:15-17, 23; Luke 1:70; 24:27, 44-45; Acts 1:16; 3:18). Similarly, Paul refers to what God had “promised beforehand through His prophets in the holy Scriptures” (Rom 1:2) and also speaks of God establishing believers both “according to my gospel and the preaching of Jesus Christ” and “by the Scriptures of the prophets, according to the commandment of the eternal God” (Rom 16:25-26; cf. 3:2; 15:4). Accordingly, Paul contends that he serves “the God of our fathers, believing everything that is in accordance with the Law and that is written in the Prophets” (Acts 24:14; cf. 23:5; John 1:45; 2 Cor 4:2). Elsewhere, Paul “for three Sabbaths reasoned with them from the Scriptures” (Acts 17:2) and “demonstrate[d] by the Scriptures that Jesus was the Christ” (Acts 18:28; cf. 1 Cor 15:3). This and other NT evidence suggests the “canonical” authority of some recognized corpus of OT Scripture.")])]), _c('a', {
    attrs: {
      "id": "page_26"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_26"
    }
  }, [_vm._v("Whereas the OT Scriptures appear to be ratified by Jesus and recognized by the apostles as, among other things, testifying of Christ (cf. John 1:45; 5:39; Acts 10:43; 1 Pet 1:10-12), Christ also commissions an enduring apostolic testimony and thus commissions (at least indirectly) the NT writings insofar as they are genuinely apostolic. Jesus himself “appointed twelve” to “be with Him” and to “send them out to preach and to have authority” (Mark 3:14-15). When sending them, Christ told them, you will bear “testimony” to “governors and kings” and “to the Gentiles” but “it is not you who speak, but it is the Spirit of your Father who speaks in you,” so that those who do not “receive you, nor heed your words” will face judgment (Matt 10:18, 20; cf. Matt 10:14-15). Elsewhere, in John 20:21, Jesus declares to the apostles, “as the Father has sent Me, I also send you” (cf. John 17:8, 18). In this regard, Christ had previously promised: “the Holy Spirit, whom the Father will send in My name, He will teach you all things, and bring to your remembrance all that I said to you” (John 14:26) and ")]), _c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("“will guide you into all the truth” (16:13). Accordingly, Christ told them, “the one who listens to you listens to me, and the one who rejects you rejects me,” suggesting their commission as Christ’s authoritative ambassadors (Luke 10:16).")])]), _c('a', {
    attrs: {
      "id": "page_27"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_27"
    }
  }, [_vm._v("Nevertheless, James Barr comments that “Jesus in his teaching is nowhere portrayed as commanding or even sanctioning the production of a written Gospel, still less a written New Testament. He never even casually told his disciples to write anything down.”")]), _c('a', {
    attrs: {
      "id": "fn31"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn31"
    }
  }, [_vm._v("31")])]), _vm._v(" However, that the NT does not portray Jesus as commanding things to be written down does not (and could not) demonstrate that he did not do so (cf. Acts 1:2). Further, even if Christ did not do so, it does not follow that the NT writings were not thereby commissioned, at least indirectly."), _c('a', {
    attrs: {
      "id": "fn32"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn32"
    }
  }, [_vm._v("32")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Here, Peter’s depiction of the apostles as “witnesses” ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("μάρτυς")]), _vm._v(") of the resurrected Christ is of considerable significance (Acts 10:41). Peter and other apostles “who ate and drank with Him [Christ] after He arose from the dead” were “chosen beforehand by God” and “ordered” by Christ “to preach to the people, and solemnly to testify ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("διαμαρτύρομαι")]), _vm._v("] that this is the One who has been appointed by God as Judge of the living and the dead” (Acts 10:41-42; cf. 2 Pet 3:2)."), _c('a', {
    attrs: {
      "id": "fn33"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn33"
    }
  }, [_vm._v("33")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("If “apostles” were (at minimum) eyewitnesses to the risen Christ and thereby appointed as his witnesses (cf. Acts 1:2-4; 9:3-15; 26:12-18), then genuinely apostolic testimony would be restricted to that of this chosen group, whose testimony would possess intrinsically “canonical” authority in virtue of their unique relationship with, and commission by, the risen Christ."), _c('a', {
    attrs: {
      "id": "fn34"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn34"
    }
  }, [_vm._v("34")])]), _vm._v(" If this "), _c('a', {
    attrs: {
      "id": "page_28"
    }
  }, [_vm._v("is correct, fulfilling Christ’s commissions regarding the worldwide spread of the gospel (not to mention the covenantal context and presumable impetus thereof) would seem to require that provision be made for the reliable and accurate transmission of this apostolic testimony after the apostles’ deaths (cf. Acts 20:29-32; 2 Pet 1:13-15). The writing down of this apostolic testimony (by apostles themselves or their close contemporary associates) appears to have been the chosen way to permanently record their unique and unrepeatable apostolic witness beyond the limits of their personal presence (cf. Rev 1:11).")]), _c('a', {
    attrs: {
      "id": "fn35"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn35"
    }
  }, [_vm._v("35")])]), _vm._v(" As such, the Great Commission by itself (Matt 28:19-20) would appear to require, and thus indirectly commission, something like the NT canon."), _c('a', {
    attrs: {
      "id": "fn36"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn36"
    }
  }, [_vm._v("36")])])]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("Canon as Divinely Commissioned")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Considerable evidence suggests that biblical authors viewed themselves as divinely commissioned and thus authoritative teachers. Whereas a claim to divine commission is, of course, not sufficient to conclude that one actually is divinely commissioned, such claims of biblical authors do indicate that a minimal intrinsic canon approach is not an external imposition.")]), _vm._v(" "), _c('p', {
    staticClass: "H3"
  }, [_vm._v("Internal NT Evidence")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Beyond the explicit NT claims regarding Christ’s commission to disseminate his teachings widely (see above), further NT evidence suggests the apostles viewed themselves and others as divinely commissioned, often explicitly in "), _c('a', {
    attrs: {
      "id": "page_29"
    }
  }, [_vm._v("virtue of their apostolic connection to the risen Christ. Jesus evidently considered his words to be an authoritative rule or standard, to be heeded and transmitted (Matt 7:24-29; 28:20; John 15:4; 17:8). Indeed, the words of Jesus, in virtue of who he was, are intrinsically authoritative and are treated thus by NT writers (1 Cor 7:10, 17; 1 Thess 4:15; Matt 28:18) and other early Christians (see chapter 3). The gospel is thus repeatedly described as the “word of God,” which the apostles are depicted as preaching and spreading (Acts 8:14; 11:1; 12:24; 13:46; 17:13; 18:11; 19:20; John 17:14; Eph 1:13) and which was recorded by eyewitnesses and contemporaries and faithfully passed down (i.e., Luke 1:1-3; cf. 24:48; John 20:31).")])]), _c('a', {
    attrs: {
      "id": "page_29"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_29"
    }
  }, [_vm._v("The author of John self-identifies as “the disciple who is testifying ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("μαρτυρέω")]), _vm._v("] to these things and wrote these things” (John 21:24; cf. 20:31), perhaps alluding to Christ’s earlier statement that the “Spirit” would be sent to the disciples and would “testify ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("μαρτυρέω")]), _vm._v("] about Me, and you "), _c('i', [_vm._v("will")]), _vm._v(" testify ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("μαρτυρέω")]), _vm._v("] also, because you have been with Me from the beginning” (John 15:26-27; cf. Luke 24:48; 1 John 1:1-2).")]), _c('a', {
    attrs: {
      "id": "fn37"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn37"
    }
  }, [_vm._v("37")])]), _vm._v(" Similarly, 1 John is presented as eyewitness testimony in writing (1:1-4) and 1 John 1:5 explicitly states: “This is the message we have heard from Him [Christ] and announce to you” (cf. Luke 1:1-3).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Further, Paul proclaims his divine commission as “an apostle” who was “not sent from men nor through the agency of man, but through Jesus Christ and God the Father” (Gal 1:1), claims to have the Spirit of God (1 Cor 7:40; cf. Rom 15:15), and emphasizes that the gospel he preaches was not “received” from “man” but received “through a revelation of Jesus Christ” (Gal 1:12; cf. 1 Cor 2:13; Eph 3:3-5; 1 Thess 2:13). Here, Paul explicitly notes that this apostolic status and message was not community-derived but divinely commissioned and revealed. As Acts 26:16 records, Christ “appoint[ed]” Paul “a minister and a witness ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("μάρτυς")]), _vm._v("]” (cf. Acts 9:1-9; 22:6-15).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The gospel preached by the apostles, itself received from the Lord, is held up as the rule and standard from which no believer is to depart (Gal 1:8-12; cf. 2 Cor 11:2-4). Accordingly, Paul exhorts his audience to “retain the standards of sound words which you have heard from me” (2 Tim 1:13; cf. 2 Thess 2:15; 3:14; 2 John 9-10; Jude 3) and hold “fast the faithful word which is in accordance with the teaching” to “exhort in sound doctrine and to refute those who contradict” (Tit 1:9). Paul further commands the brethren “in the name of "), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }, [_vm._v("our Lord Jesus Christ” to “keep away” from those who live “not according to the tradition which you received from us” and “not associate” with “anyone” who “does not obey our instruction in this letter (2 Thess 3:6, 14; cf. Gal 1:8; 2 John 10).")])]), _c('a', {
    attrs: {
      "id": "page_30"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_30"
    }
  }, [_vm._v("The Bereans recognized “the word of God” that was “proclaimed by Paul” by “examining the Scriptures daily "), _c('i', [_vm._v("to see")]), _vm._v(" whether these things were so” (Acts 17:11, 13).")]), _c('a', {
    attrs: {
      "id": "fn38"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn38"
    }
  }, [_vm._v("38")])]), _vm._v(" Accordingly, Paul describes the apostolic teaching of himself and others as not merely “the word of men” but “the word of God” (1 Thess 2:13). That the authority of Paul’s teachings was not limited to oral proclamation is explicit in that Paul commands “by the Lord” that his letters be read to others (1 Thess 5:27; cf. Col 4:16; Rev 1:3), exhorts to “stand firm and hold to the traditions"), _c('a', {
    attrs: {
      "id": "fn39"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn39"
    }
  }, [_vm._v("39")])]), _vm._v(" which you were taught, whether by word "), _c('i', [_vm._v("of mouth")]), _vm._v(" or by letter from us” (2 Thess 2:15; cf. 3:14), and calls his readers to “recognize that the things which I write to you are the Lord’s commandment” (1 Cor 14:37; cf. 38). 1 Corinthians 14:37 is representative of numerous examples where NT writers communicate their teachings as by the authority of Jesus himself (cf. 1 Thess 4:2-3, 8; 5:27).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Further, John is explicitly commissioned to “write in a book what” he sees “and send "), _c('i', [_vm._v("it")]), _vm._v(" to the seven churches” (Rev 1:11). He depicts the content of his writing as the “revelation of Jesus Christ, which God gave Him to show to His bond-servants” (Rev 1:1), wherein he “testified to the word of God and to the testimony of Jesus Christ” (Rev 1:2). Revelation thus testifies of itself as direct revelation from God and further evinces its “canonical” authority by concluding with an inscriptional curse against anyone who “adds to” or “takes away from” the “words of the book of this prophecy” (Rev 22:18-19; cf. Deut 4:2).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Finally, NT writers appear to treat other NT writings as “Scripture” in two noteworthy instances: 1 Tim 5:18 quotes as “Scripture” the same words that appear in Luke 10:7 ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ἄξιοϛ . . . ὁ ἐργάτης τοῦ μισθοῦ αὐτοῦ")]), _vm._v("), alongside a quotation from Deut 25:4."), _c('a', {
    attrs: {
      "id": "fn40"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn40"
    }
  }, [_vm._v("40")])]), _vm._v(" Further, 2 Pet 3:15-16 declares the writings of Paul to be “Scripture” alongside “the rest of the Scriptures,” thus identifying a group "), _c('a', {
    attrs: {
      "id": "page_31"
    }
  }, [_vm._v("of Pauline letters, with which he appears to suppose his audience is familiar, as (minimally) “canonical.”")]), _c('a', {
    attrs: {
      "id": "fn41"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn41"
    }
  }, [_vm._v("41")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("In a similar vein, the early Christians are depicted as “continually devoting themselves to the apostles’ teaching” (Acts 2:42) and there is strong evidence that the early post-apostolic Christians regarded genuinely apostolic writings as holding “canonical” authority in virtue of their apostolicity (see chapter 3). In all this, the NT records an abundance of self-testimony regarding its divine commission and attendant authority (i.e., its minimal “canonicity”), which points toward Kruger’s conclusion that “the New Testament writings, generally speaking, were intended to be documents with an authority equivalent to that of Scripture.”"), _c('a', {
    attrs: {
      "id": "fn42"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn42"
    }
  }, [_vm._v("42")])])]), _vm._v(" "), _c('p', {
    staticClass: "H3"
  }, [_vm._v("Internal OT Evidence")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Beyond the evidence noted above that the NT writers viewed some corpus of (OT) Scripture as “canonical” (in the minimal sense, cf. 2 Tim 3:16; 2 Pet 1:20-21),"), _c('a', {
    attrs: {
      "id": "fn43"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn43"
    }
  }, [_vm._v("43")])]), _vm._v(" the OT yields considerable information about its status as divinely commissioned Scripture. Throughout the OT, writers are divinely commissioned to write down laws and/or teachings with continuing covenantal authority (Exod 17:14; Deut 6:4-8; 31:9, 12; Josh 1:8; 23:6; 1 Kgs 2:3; Neh 8:8-18; 9:3; Jer 30:2; cf. Deut 4:2)."), _c('a', {
    attrs: {
      "id": "fn44"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn44"
    }
  }, [_vm._v("44")])]), _vm._v(" Thus, God commanded Moses that his covenantal revelation be written, preserved, and passed on (Exod 17:14; 24:4; 31:18; 34:27; Deut 10:5; 29:29; 31:9-12, 25-26). Moses not only proclaimed the “words of the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v("” to the people but also “wrote all the words of the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v("” (Exod 24:3-4). After Moses, other divinely commissioned and inspired prophets carried on the writing of divine revelation (e.g., Josh 24:26; 1 Sam 10:25; Isa 30:8). For example, Jeremiah is commanded: “Thus says the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(", the God of Israel, ‘Write all the words which I have spoken to you in a book’ ” (30:2). Similarly, the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(" commanded Habakkuk to “record the vision and inscribe it on tablets” for future readers (2:2-3).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_32"
    }
  }, [_vm._v("Later OT writers recognized and referred to earlier Scriptures as authoritative (Josh 23:6; 1 Kgs 2:3; 2 Kgs 23:1-3; Ezra 3:2; Neh 8–9; Jer 26:18; Ezek 14:14, 20; Dan 9:2; Mic 4:1-3; 2 Chr 35:6, etc.). The prophets continually called the people to “hear the word of the Lord,” which was to function as their covenantal rule of faith and practice (Deut 11:22; Josh 22:5; Amos 3:1; Jer 2:4; Ezek 6:3; Hos 4:1; cf. Zech 7:12; Neh 9:30). Finally, OT writers frequently use phrases that explicitly denote their divine commission of Scripture as prophetic. These include “by the hand of,” “the Word of the Lord,” “declares the Lord,” and “thus says the Lord.”")]), _c('a', {
    attrs: {
      "id": "fn45"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn45"
    }
  }, [_vm._v("45")])]), _vm._v(" According to Gerhard Hasel, such phrases are “the OT’s way of saying that it is God-derived and ‘God-breathed’ ” and, as such, intrinsically “canonical.”"), _c('a', {
    attrs: {
      "id": "fn46"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn46"
    }
  }, [_vm._v("46")])]), _vm._v(" With this abundant testimony of biblical writers regarding divine commission and authority in mind, how might we go about further discerning "), _c('i', [_vm._v("which books")]), _vm._v(" show evidence of this divine commission?")]), _vm._v(" "), _c('p', {
    staticClass: "H1"
  }, [_vm._v("C"), _c('span', {
    staticClass: "small"
  }, [_vm._v("RITERIA IN THE")]), _vm._v(" R"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ECOGNITION OF THE")]), _vm._v(" C"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ANON OF")]), _vm._v(" S"), _c('span', {
    staticClass: "small"
  }, [_vm._v("CRIPTURE")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Intrinsically canonical books, in virtue of their divinely commissioned nature, possess some traits that assist in recognizing them as “canonical.” In particular, we might identify three criteria of canon recognition: books must be (1) divinely commissioned as prophetic and/or apostolic, (2) consistent with past “canonical” revelation, and (3) self-authenticating. Here, “criteria” is used to mean characterizing marks or traits, the recognition of which does not impose canonicity on books or suggest that any recognizer possesses authority to judge the canon. The criteria merely manifest which books should be properly accepted as “canonical” in virtue of being divinely commissioned. These criteria of recognition are grounded in the canonical data (see below) and there is evidence that at least minimal forms of them were used (at times) in the history of canon recognition."), _c('a', {
    attrs: {
      "id": "fn47"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn47"
    }
  }, [_vm._v("47")])])]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_vm._v("Propheticity and Apostolicity")])]), _c('a', {
    attrs: {
      "id": "page_33"
    }
  }), _c('p', {
    staticClass: "Bodyflush1"
  }, [_c('a', {
    attrs: {
      "id": "page_33"
    }
  }, [_vm._v("If books are “canonical” in virtue of divine commission, it follows that those seeking to recognize “canonical” books should look for evidence of divine commission. As seen above, the canon identifies prophets and apostles as divinely commissioned and thus “canonically” authoritative messengers of divine revelation. God sent “prophets and apostles” (Luke 11:49; cf. Rom 16:25-26) and the very “household of God” is “built on the foundation of the apostles and prophets, Christ Jesus Himself being the "), _c('i', [_vm._v("corner")]), _vm._v(" stone” (Eph 2:19-20; cf. 3:3-5; Rev 22:6). Accordingly, Christians are exhorted to “remember the words spoken beforehand by the holy prophets and the commandment of the Lord and Savior "), _c('i', [_vm._v("spoken")]), _vm._v(" by your apostles” (2 Pet 3:2; cf. 2 Kgs 17:13; 2 Chr 24:19; Jer 7:25; 25:4; Zech 7:12; Luke 1:70; Acts 3:18, 21; Rom 1:2; Heb 1:1-2).")]), _c('a', {
    attrs: {
      "id": "fn48"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn48"
    }
  }, [_vm._v("48")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("As such, a genuinely “canonical” book must be the written record of covenantal prophetic and/or apostolic testimony, written either by a prophet/apostle or a close contemporary associate thereof."), _c('a', {
    attrs: {
      "id": "fn49"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn49"
    }
  }, [_vm._v("49")])]), _vm._v(" Regarding the OT, a prophet is, by definition, one divinely authorized to speak for God (i.e., God’s commissioned spokesperson, Jer 15:19; Luke 1:70; Acts 3:18, 21; cf. Deut 18:20). Accordingly, “prophecy of Scripture” is not “made by an act of human will, but men moved by the Holy Spirit spoke from God” (2 Pet 1:20-21; cf. 2 Tim 3:16). As seen earlier, the NT writers treat the “Scriptures of the prophets” (cf. Matt 26:56; Rom 1:2; 16:26) as a “rule” or “standard” (i.e., “canonical”); Jesus "), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_vm._v("himself frequently appeals to the Law and the Prophets as authoritative (see Matt 5:17; cf. 23:37; Luke 13:34).")])]), _c('a', {
    attrs: {
      "id": "page_34"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_34"
    }
  }, [_vm._v("Properly recognizing genuine covenantal prophets is, then, crucial.")]), _c('a', {
    attrs: {
      "id": "fn50"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn50"
    }
  }, [_vm._v("50")])]), _vm._v(" Accordingly, Christians are exhorted to “test the spirits to see whether they are from God, because many false prophets have gone out into the world” (1 John 4:1; cf. Matt 7:15; Deut 18:20; Jer 7:25; 14:14; Ezek 13:6). Not only does this criterion appear to be in use (at least minimally) in the NT, the first-century Jewish historian Josephus also appears to employ something like it. In "), _c('i', [_vm._v("Against Apion")]), _vm._v(" 1.7, 8, he references a three-part corpus of “our books, those which are justly accredited” as of “the prophets” who wrote through “inspiration.” Josephus numbers them “but two and twenty” and specifies that the authoritative writings were “until Artaxerxes,” saying, “From Artaxerxes to our own time the complete history has been written, but has not been deemed worthy of equal credit with the earlier records, because of the failure of the exact succession of the prophets.”"), _c('a', {
    attrs: {
      "id": "fn51"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn51"
    }
  }, [_vm._v("51")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Some have taken Josephus to mean that prophecy (at least of the covenantal kind deemed Scripture) had ceased such that “canonical” books would be limited to those written by the mid-5th century BC."), _c('a', {
    attrs: {
      "id": "fn52"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn52"
    }
  }, [_vm._v("52")])]), _vm._v(" This would complement the view that appears to be reflected in many Jewish texts from the Second Temple and rabbinic periods that Israelite prophecy ceased around the beginning of the Second Temple period."), _c('a', {
    attrs: {
      "id": "fn53"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn53"
    }
  }, [_vm._v("53")])]), _vm._v(" For instance, 1 Macc 9:27 refers to a time of “great distress in Israel, such as had not been since the time that prophets ceased to appear among them.”"), _c('a', {
    attrs: {
      "id": "fn54"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn54"
    }
  }, [_vm._v("54")])]), _vm._v(" Whether or not Josephus believed prophecy "), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_vm._v("had ceased, he ascribes special authority to prophetic writings in the line from Moses to the time of Artaxerxes (ca. 465-424 BC), corresponding to the internal biblical testimony regarding the historical context of the last books of the Hebrew Bible and to the time that 2 Macc 2:13 has Nehemiah collecting “the books about the kings and prophets.”")])]), _c('a', {
    attrs: {
      "id": "page_35"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_35"
    }
  }, [_vm._v("Similarly, the NT writings must consist of the testimony of apostles — those eyewitnesses to the risen Christ appointed as his witnesses (cf. Acts 1:2-4; 9:3-15; 10:41-43; 26:12-18). Accordingly, “the New Testament is filled with references to the apostles as Christ’s foundational witnesses” (Luke 24:48; Acts 1:8, 22; 3:15; 5:32; 10:39-41; 26:16; cf. Eph 2:20).")]), _c('a', {
    attrs: {
      "id": "fn55"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn55"
    }
  }, [_vm._v("55")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Genuinely "), _c('i', [_vm._v("apostolic")]), _vm._v(" testimony consists of direct witness to Christ and, therefore, must be written during the time of the apostles."), _c('a', {
    attrs: {
      "id": "fn56"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn56"
    }
  }, [_vm._v("56")])]), _vm._v(" If F. F. Bruce is correct that the “four Gospels belong to the decades between 60 and 100, and it is to these decades too that all (or nearly all) the other New Testament writings are to be assigned,” the NT writings possess the proper antiquity."), _c('a', {
    attrs: {
      "id": "fn57"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn57"
    }
  }, [_vm._v("57")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_36"
    }
  }, [_vm._v("Conversely, the Muratorian Fragment rejects "), _c('i', [_vm._v("The Shepherd of Hermas")]), _vm._v(" because it was written “after their [the apostles’] time.”")]), _c('a', {
    attrs: {
      "id": "fn58"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn58"
    }
  }, [_vm._v("58")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Here and elsewhere in the history of canon recognition, the importance of the writer’s close connection to Jesus, either firsthand or by direct association with Christ’s apostles, is apparent (see chapter 3)."), _c('a', {
    attrs: {
      "id": "fn59"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn59"
    }
  }, [_vm._v("59")])]), _vm._v(" Within the NT itself, Paul repeatedly emphasizes his handwriting to mark letters as truly from him, demonstrating the significance of apostolic origin (1 Cor 16:21; Gal 6:11; Col 4:18; 2 Thess 3:17; Phlm 19). Such a “temporary criterion” suggests both wariness of potentially pseudonymous writings and that the original recipients knew Paul."), _c('a', {
    attrs: {
      "id": "fn60"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn60"
    }
  }, [_vm._v("60")])]), _vm._v(" Likewise, whereas many NT writings are anonymous, it is likely that their original recipients knew the authors and thus could vouch for their authentic apostolicity. This seems even more likely when one notes the NT insistence on properly recognizing divinely commissioned messengers over and against “many false prophets” (1 John 4:1; cf. Jer 14:14; see chapter 3).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_vm._v("Given that historical certitude is beyond our reach, however, caution should be exercised in the application of the prophetic/apostolic criterion. While important historical referents may greatly assist in the recognition of canonical books, the historical data regarding propheticity/apostolicity is variously interpreted and should not be expected to settle the matter of canonicity. In this regard, it is helpful to consider two further criteria that themselves impinge upon whether a given book should be confidently recognized as prophetic and/or apostolic.")])]), _c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_c('p', {
    staticClass: "H2"
  }, [_vm._v("Consistency with Past Revelation")])]), _c('p', {
    staticClass: "Bodyflush1"
  }, [_c('a', {
    attrs: {
      "id": "page_37"
    }
  }, [_vm._v("The second crucial trait of canonical writings is consistency with past revelation.")]), _c('a', {
    attrs: {
      "id": "fn61"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn61"
    }
  }, [_vm._v("61")])]), _vm._v(" From early in Israel’s history, the Law (of Moses) was set forth as the divinely revealed rule of faith and practice and subsequent revelation was to be tested thereby (cf. Deut 13:1-5). As such, any genuine new revelation would not contradict any genuine previous revelation."), _c('a', {
    attrs: {
      "id": "fn62"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn62"
    }
  }, [_vm._v("62")])]), _vm._v(" In this regard, Isaiah 8:20 exhorts: “To the law and to the testimony! If they do not speak according to this word, it is because they have no dawn” (cf. v. 16). That is, new light from God will not contradict old light (Num 23:19; 1 Sam 15:29; Deut 4:1-2; Mal 3:6; Matt 5:17-18; 24:35; Tit 1:2-3; Heb 6:17-18; 13:7-9).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Application of this criterion of recognition in post-apostolic times would be analogous to the way those living while the canon was in process should have “test[ed] the spirits to see whether they are from God” (1 John 4:1). Israel was expected to do this in OT times (Jer 14:14; cf. Luke 6:26) and Christ and the apostles called on their contemporaries to do so in NT times (Matt 7:15; 24:44; Mark 13:22-23; 2 Pet 2:1). Implementation of such testing is exemplified by the Bereans, who “received the word with great eagerness, examining the Scriptures daily "), _c('i', [_vm._v("to see")]), _vm._v(" whether these things were so” (Acts 17:11), and in Paul’s statement that he believes “everything that is in accordance with the Law and that is written in the Prophets” (Acts 24:14). Christians were further exhorted to “hold fast the faithful word which is in accordance with the teaching” and to refute “those who contradict” (Tit 1:9; cf. Gal 1:8-12; 2 Cor 11:2-4; 2 Tim 1:13; 2 Thess 2:15; 3:6, 14; 2 John 9-10; Jude 3).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_38"
    }
  }, [_vm._v("It follows from this and the requisite of divine commission generally that only writings that are consistent with one another could be properly “canonical.”")]), _c('a', {
    attrs: {
      "id": "fn63"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn63"
    }
  }, [_vm._v("63")])]), _vm._v(" This criterion of consistency alone rules out many writings that have been put forth as prophetic or apostolic. Since evaluation regarding whether different texts are consistent depends upon how such texts are interpreted, it appears to be impossible to "), _c('i', [_vm._v("prove")]), _vm._v(" conclusively that the “canon” is internally consistent. The criterion of consistency, however, may nevertheless exclude some writings that are put forth as claimants, insofar as they demonstrably contradict previous revelation (e.g., the so-called "), _c('i', [_vm._v("Gospel of Thomas")]), _vm._v(")."), _c('a', {
    attrs: {
      "id": "fn64"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn64"
    }
  }, [_vm._v("64")])])]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("Self-Authentication of Divine Commission")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Although we can recognize characteristics of books that give us confidence in canon recognition, canonical books are finally self-authenticating (cf. John 7:17; 10:27; 1 Cor 2:10-14). The intrinsic canonicity of writings derives from divine action. Thus, whereas information regarding the human authorship of books provides important historical indicators, far more important is the divine authorship of “canonical” books.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Revelation and inspiration are, therefore, prerequisites of canonical writings."), _c('a', {
    attrs: {
      "id": "fn65"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn65"
    }
  }, [_vm._v("65")])]), _vm._v(" However, not every inspired writing is thereby canonical. In numerous instances, the Bible refers to prophetic and apostolic writings that are not part of the canon."), _c('a', {
    attrs: {
      "id": "fn66"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn66"
    }
  }, [_vm._v("66")])]), _vm._v(" Other books, such as "), _c('i', [_vm._v("Shepherd of Hermas")]), _vm._v(", were considered by "), _c('a', {
    attrs: {
      "id": "page_39"
    }
  }, [_vm._v("some to be inspired but were not recognized as canonical because they did not meet other criteria, such as apostolicity.")]), _c('a', {
    attrs: {
      "id": "fn67"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn67"
    }
  }, [_vm._v("67")])]), _vm._v(" Inspiration is, then, a necessary but not sufficient characteristic of canonicity."), _c('a', {
    attrs: {
      "id": "fn68"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn68"
    }
  }, [_vm._v("68")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("There are important external and internal signs in canonical books, which have guided their recognition as canon and are still useful to us in our own acceptance of what God has done. That such books were at least recognized as worthy of preservation is evidenced in their extremely unusual level of preservation by the community."), _c('a', {
    attrs: {
      "id": "fn69"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn69"
    }
  }, [_vm._v("69")])]), _vm._v(" Yet, "), _c('i', [_vm._v("intrinsic")]), _vm._v(" canonicity ultimately stems from the activity of God, not humans. As such, recognition of the intrinsic canon requires numerous faith decisions, though not blind ones, supported via faith-based interpretation of the available data (as is any other belief). Illumination by the Holy Spirit regarding such decisions is paramount (1 Cor 2:14).")]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("A Notable Corollary")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Some view community usage of writings as an important criterion of canonicity. It is of prime significance to the community canon model, particularly the version that stresses the adaptability of the canon to the community."), _c('a', {
    attrs: {
      "id": "fn70"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn70"
    }
  }, [_vm._v("70")])]), _vm._v(" Community consensus is commonly offered as a criterion or attribute of canonicity on the view that the Holy Spirit directed the Christian community’s recognition of the authentic canon."), _c('a', {
    attrs: {
      "id": "fn71"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn71"
    }
  }, [_vm._v("71")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_40"
    }
  }, [_vm._v("While I do believe that God guided Christians in recognizing the canon,")]), _c('a', {
    attrs: {
      "id": "fn72"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn72"
    }
  }, [_vm._v("72")])]), _vm._v(" it is self-evident that God did not guide each self-identifying Christian community to identify the same canon."), _c('a', {
    attrs: {
      "id": "fn73"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn73"
    }
  }, [_vm._v("73")])]), _vm._v(" Thus, the application of usage or consensus as a criterion would first require deciding which community God (sufficiently) guided in canon recognition or what degree of consensus regarding canonicity is sufficient and during what time period. Yet, adoption of a "), _c('i', [_vm._v("particular")]), _vm._v(" community as standard raises a host of questions (see chapter 3).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("If one does not propose a "), _c('i', [_vm._v("particular")]), _vm._v(" community as adequate in this regard, questions arise regarding the requisite extent of consensus. Is a simple majority sufficient? Even if something like an overwhelming majority is posited, the consensus view appears to require something like an adaptable canon (even if unintentionally) given that community consensus is malleable. One might argue that at certain junctures of past Christian history, differing scopes of the canon would be held by the broadest consensus of Christians (e.g., the Council of Trent). Even if it could be shown that this is not the case historically, it appears that nothing would guarantee that such consensus could not change in the future (as some scholars advocate).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("While such a criterion is consistent with community canon approaches, it contradicts the intrinsic canon model, which holds that the canon just "), _c('i', [_vm._v("is")]), _vm._v(" those books that God deems canonical. Whereas one would expect canonical books to be preserved such that usage might be a product of canonicity, usage does not itself bear on whether a book is intrinsically canonical. As such, community recognition cannot be a proper trait of canonicity (where trait is something intrinsic to the nature of “canonical” writing) because canonicity does not depend upon community recognition."), _c('a', {
    attrs: {
      "id": "fn74"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn74"
    }
  }, [_vm._v("74")])]), _vm._v(" Jesus was and is the Son of God whether or not his community recognizes him to be so. Similarly, the intrinsic canon is the canon whether or not any particular community recognizes it to be so (cf. Rom 3:3-4).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Nevertheless, while I do not view community usage/consensus as a proper trait of canonicity, I do believe that the data regarding historical usage provides extremely valuable (but not determinative) evidence relative to canon recognition. For instance, the NT books were so widely used that nearly the entire "), _c('a', {
    attrs: {
      "id": "page_41"
    }
  }, [_vm._v("NT could be reconstructed from the writings of 2nd- to 4th-century church fathers.")]), _c('a', {
    attrs: {
      "id": "fn75"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn75"
    }
  }, [_vm._v("75")])]), _vm._v(" In this and other respects, the community’s witness throughout the ages provides powerful supportive evidence that coheres with other available evidence regarding canonicity. Although there is diversity regarding the scope of the canon, I do not believe it is a coincidence that there is a common canonical core of sixty-six books that is accepted by nearly all self-identifying Christians. Whereas some view more than sixty-six books as canonical, very few accept fewer than the thirty-nine books of the OT and the twenty-seven books of the NT.")]), _vm._v(" "), _c('p', {
    staticClass: "H1"
  }, [_vm._v("C"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ONFIDENT")]), _vm._v(" R"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ECOGNITION OF")]), _vm._v(" C"), _c('span', {
    staticClass: "small"
  }, [_vm._v("LOSED")]), _vm._v(" C"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ANON?")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Which books then are properly recognized as canonical?"), _c('a', {
    attrs: {
      "id": "fn76"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn76"
    }
  }, [_vm._v("76")])]), _vm._v(" Questions regarding the nature and scope of the canon are exceedingly complex and hinge upon many decisions and interpretations regarding various questions and points of data, the complexity of which cannot be done justice to in this brief treatment."), _c('a', {
    attrs: {
      "id": "fn77"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn77"
    }
  }, [_vm._v("77")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Given our individual and collective epistemic limitations, fallible judgment, and the often murky nature of historical data, the intrinsic canon approach does not expect or require anything like absolutely certain or indubitable conclusions regarding canonicity. What is sought is "), _c('i', [_vm._v("confident")]), _vm._v(" recognition of divinely commissioned covenantal writings; seeking the best explanation of the available evidence given the minimal Christian belief that God has revealed himself to humans, most fully in Christ, in a way that is recognizable."), _c('a', {
    attrs: {
      "id": "fn78"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn78"
    }
  }, [_vm._v("78")])]), _vm._v(" Based on my own investigation, I believe that the thirty-nine books of the OT and the twenty-seven books of the NT do in fact meet the criteria of recognition (i.e., they possess intrinsically canonical traits) and thus make up the divinely commissioned canon, authoritative for all faith and practice.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_vm._v("It is, of course, beyond the scope of this work to explain sufficiently the application of the criteria to each individual book. I am convinced that the sixty-six books of the canon possess all three traits of canonicity, but it may be helpful to address briefly the first criterion. First, note that the recognition of the canonical books does not hinge upon identification of the authors in every case; it is sufficient to have reason to believe the books are prophetic or apostolic. While disputes abound regarding the authorship and dating of many biblical books, many books yield internal evidence regarding their prophetic or apostolic nature that merit due consideration (such is given priority on the intrinsic canon approach while speculative reconstructions are given little weight, see page 35, note 57).")])]), _c('a', {
    attrs: {
      "id": "page_42"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_42"
    }
  }, [_vm._v("With this in mind, there is strong evidence (particularly with, but not limited to, the internal evidence of the books) supporting the propheticity and apostolicity of the sixty-six books of the Protestant canon, reinforced (but not determined) by both Judaism’s and Christianity’s overwhelming recognition of at least these same books. With regard to the NT, insofar as the so-called Pauline epistles are, in fact, Pauline, they are thereby apostolic. These epistles make explicit claims to Pauline authorship (Rom 1:1; 1 Cor 1:1; 2 Cor 1:1; Gal 1:1; Eph 1:1; Phil 1:1; Col 1:1; 1 Thess 1:1; 2 Thess 1:1; 1 Tim 1:1; 2 Tim 1:1; Tit 1:1; Phlm 1), usually alongside claims to Paul’s apostolicity (except Philippians, 1 and 2 Thessalonians, and Philemon). Hebrews, traditionally viewed as Pauline, is anonymous and its authorship is heavily disputed. Yet, whatever one concludes regarding authorship, Hebrews itself appears to claim apostolicity (Heb 2:3-4; 13:23).")]), _c('a', {
    attrs: {
      "id": "fn79"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn79"
    }
  }, [_vm._v("79")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The gospels are also anonymous, but they too offer internal reasons for recognizing them as apostolic. Even apart from the tradition of authorship by apostles (Matthew, John) and close associates thereof (Mark, Luke, see page 33, note 49), the gospels lay claim to eyewitness (apostolic) testimony or write as personal (apostolic) witnesses (cf. Luke 1:1-3; John 15:26-27; 20:30-31; 21:24). The very term “gospel” itself was likely “a reference to the authoritative message of the apostolic preaching.”"), _c('a', {
    attrs: {
      "id": "fn80"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn80"
    }
  }, [_vm._v("80")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("This leaves the so-called general epistles and Revelation. Both 1 and 2 Peter explicitly claim to be written by the apostle Peter (1 Pet 1:1; 2 Pet 1:1, 16; cf. 2 Pet 3:2). 1 John puts itself forth as eyewitness testimony (thus appearing to claim "), _c('a', {
    attrs: {
      "id": "page_43"
    }
  }, [_vm._v("apostolicity, 1 John 1:1-5),")]), _c('a', {
    attrs: {
      "id": "fn81"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn81"
    }
  }, [_vm._v("81")])]), _vm._v(" which would extend to 2 and 3 John if they are indeed written by the same author. James and Jude are thought to be written by brothers of Jesus, thus directly connected to Christ himself — Jude’s author presents himself as the brother of James (Jude 1) and James presents himself simply as “a servant ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("δοῦλος")]), _vm._v("] of God and of the Lord Jesus Christ” (1:1). Finally, Revelation explicitly claims to be the revelation of Jesus Christ and thus divinely commissioned as prophetic testimony (Rev 1:1-11).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The OT is even more complex than the NT and I thus make no attempt to survey each of the books. Here, consider the abundance of self-testimony regarding the propheticity of the OT contents (see the brief survey earlier). Note also the considerable testimony of Jesus and the apostles (including usage and the manner of such usage, see page 25, note 29), which I take to sufficiently ratify most (if not all) of the thirty-nine books of the OT Scripture in a way that appears to cohere with the evidence regarding Judaism’s recognition of the Hebrew Bible.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Of course, no amount of internal or external evidence could place the propheticity or apostolicity of writings beyond any doubt. In my view, however, an ancient book’s internal claim to propheticity or apostolicity counts as evidence unless there is sufficient counterevidence to defeat (or strongly question) such a claim and I believe that while there is sufficient evidence against all other known books, there is insufficient extant evidence against the propheticity or apostolicity of the sixty-six books of the canon, which show considerable evidence of their divine commission (relative to all three criteria)."), _c('a', {
    attrs: {
      "id": "fn82"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn82"
    }
  }, [_vm._v("82")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Conversely, all other books of which I am aware fail to meet the criteria of recognition. Indeed, I am aware of no claimants beyond the sixty-six aforementioned books that are genuinely (covenantally) prophetic or apostolic. Further, some prospective claimants in this regard demonstrably fail to meet the second criterion, consistency with past revelation."), _c('a', {
    attrs: {
      "id": "fn83"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn83"
    }
  }, [_vm._v("83")])]), _vm._v(" Finally, and in an overarching sense, I believe that the sixty-six books evince a unique quality that self-authenticates them, which I do not find in any other claimants.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_44"
    }
  }, [_vm._v("As noted above, (at least) these same sixty-six books have been recognized by the majority of Christians throughout the ages. Judaism and Christianity nearly unanimously agree that the thirty-nine OT books (or twenty-four as they are enumerated in the Hebrew Bible) are canonical. Further, the twenty-seven books of the NT are nearly unanimously accepted as canonical throughout Christianity. Among most Christians, then, the debate concerns not whether the thirty-nine OT books and twenty-seven NT books are genuinely canonical but whether there are other books that should be viewed as canonical, particularly the OT Apocrypha.")])]), _c('a', {
    attrs: {
      "id": "page_44"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_44"
    }
  }, [_vm._v("The reasons that Protestants give for excluding the OT Apocrypha from the biblical canon include: (1) the evidence appears to fall considerably against the propheticity of the Apocrypha given that, among other things, Jesus and the apostles do not appear to treat any OT Apocryphal books as Scripture whereas there is considerable evidence regarding their treatment of nearly all of the twenty-four books of the Hebrew Bible as “Scripture” (cf. the first-century writings of Philo, who quotes extensively from the OT but never from the NT); (2) the apparent belief of Second Temple Judaism and beyond that (at least Scriptural) prophecy had ceased in the Persian period (cf. 1 Macc 9:26-27), which Josephus suggests is the reason the Jews did not view such books as authoritative.")]), _c('a', {
    attrs: {
      "id": "fn84"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn84"
    }
  }, [_vm._v("84")])]), _vm._v(" As Blomberg notes, “the earliest complete or nearly complete New Testament manuscripts still in existence (from the fourth and fifth centuries) have the LXX attached to them” and include the Apocrypha. Some suggest this is evidence that “Hellenistic or Diaspora Judaism thus must have had an expanded canon.” However, the “actual discussions of the contents of Scripture in ancient Judaism show no trace of an expanded canon that included the Apocrypha.”"), _c('a', {
    attrs: {
      "id": "fn85"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn85"
    }
  }, [_vm._v("85")])]), _vm._v(" He concludes that “no "), _c('i', [_vm._v("Jews")]), _vm._v(" ever seriously supported the canonization of any of the Apocrypha.”"), _c('a', {
    attrs: {
      "id": "fn86"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn86"
    }
  }, [_vm._v("86")])]), _vm._v(" Further, many Protestants contend that the books fail the test of consistency with past revelation (e.g., 2 Macc 12:43-45) and self-authentication.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("On the intrinsic canon approach, taken as a cumulative case, I believe that the available evidence suggests that only the sixty-six books of Scripture can be confidently recognized as canonical; that is, no other extant books possess the traits of canonicity."), _c('a', {
    attrs: {
      "id": "fn87"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn87"
    }
  }, [_vm._v("87")])]), _vm._v(" However, what if a lost letter of Paul suddenly surfaced? "), _c('a', {
    attrs: {
      "id": "page_45"
    }
  }, [_vm._v("Would not a genuinely Pauline letter meet the three criteria and, if so, would this not mean that other books beyond the sixty-six books might yet be canonical? This hypothetical scenario is difficult to judge in the abstract because the contents of any purportedly Pauline writing that might surface would impinge upon whether the second or third criteria might be met. Further, such a scenario would face the practically insoluble problem of lacking the historical evidence needed for confident recognition.")])]), _c('a', {
    attrs: {
      "id": "page_45"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_45"
    }
  }, [_vm._v("Recognition of genuine apostolicity in the twenty-first century necessarily depends upon historical evidence, including but not limited to the various ancient manuscripts that have been passed down and extant writings of other ancient writers regarding such writings. At best, it seems to me, one might remain agnostic about whether such a book was genuinely Pauline. Whereas all of the sixty-six canonical books might be recognized on the basis of not only the contents of their writing but also other extant historical evidence regarding their provenance, usage, and recognition, and the crucial preservation of numerous manuscripts that evince accurate transmission,")]), _c('a', {
    attrs: {
      "id": "fn88"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn88"
    }
  }, [_vm._v("88")])]), _vm._v(" any purportedly Pauline book would lack such a historical record and thus the basis for confident recognition."), _c('a', {
    attrs: {
      "id": "fn89"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn89"
    }
  }, [_vm._v("89")])]), _vm._v(" Without implying any authority of communities regarding canonicity, this should remind us of the crucial role of the community in the recognition of the canon, particularly in passing down and thus making available to future generations the means by which confident recognition might take place."), _c('a', {
    attrs: {
      "id": "fn90"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn90"
    }
  }, [_vm._v("90")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_46"
    }
  }, [_vm._v("One might ask further, however, why one should adopt or recognize a “closed” canon? As noted earlier, some within the community canon model favor an adaptable canon such that the contents of the canon are continually subject to change via community determination. On an intrinsic canon approach, however, if the OT and NT contain sufficient covenant revelation of God’s activity in the history of salvation, the canon would be fittingly closed by the NT writings.")]), _c('a', {
    attrs: {
      "id": "fn91"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn91"
    }
  }, [_vm._v("91")])]), _vm._v(" On the view that Jesus himself provided the ultimate revelation of God to humanity, and given that the apostles were commissioned as the uniquely elect witnesses to this revelation, the canon would be closed by the last written apostolic testimony and thus by the time of the death of the last living apostles. Alongside the OT Scriptures of the prophets, then, insofar as the NT includes the authentic divinely commissioned apostolic writings and thus sufficient witness to the teachings, life, death, and resurrection of Christ, no revelation thereafter could be the rule or standard (i.e., canonical) because the ruler himself had come, fulfilling (but not nullifying) the OT, himself being the ultimate canonical revelation, and commissioning the NT witness thereof via his apostles.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("In this regard, Jesus promised that the Holy Spirit would guide the apostles into “all truth” (John 16:13), suggesting the sufficiency of the apostolic witness (cf. John 20:31; 21:25). Further, the NT depicts Christ as fulfilling, without nullifying, the OT as himself the full revelation of God (cf. Matt 5:17-18; John 14:9). In this regard, if the NT apostolic testimony to the Christ event is properly understood as the written document of the new covenant, it is possible that the inscriptional curse that nothing should be added or taken away at the end of Revelation (22:18-19) applies not only to that book alone, but to the NT covenantal witness itself (effectively signaling the close of the canon).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Whether or not the inscriptional curse of Revelation is taken to effectively close the canon, the authentic apostolic witness to the Christ event, inspired by the Holy Spirit, is sufficient to effectively close the canon. In this regard, Christ promised the apostles that the Holy Spirit would “teach” them “all things, and bring to” their “remembrance all that” Christ said to them (John 14:26). The prophetic voice of the OT and NT continues to the eschaton and there is no need for further covenant revelation. This does not mean that the Holy Spirit no longer bestows the prophetic gift, but that no post-apostolic prophet will "), _c('a', {
    attrs: {
      "id": "page_47"
    }
  }, [_vm._v("be canonical and must rather be tested by the canon, which includes the full revelation of God that culminated in the God-man Jesus Christ. In this way the Spirit continues to speak, but always in accord with God’s previous revelation in the canon of Scripture (1 John 4:1; Isa 8:20).")])]), _c('a', {
    attrs: {
      "id": "page_47"
    }
  }, [_c('p', {
    staticClass: "H1"
  }, [_vm._v("C"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ONCLUSION")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("In this chapter I explored the definition and scope of the biblical canon from a theological perspective. Whereas in the community canon model the community determines canonicity, in the intrinsic canon model writings are canonical in virtue of their divine commission (broadly construed) such that divine action makes the canon “canonical.” Against this background, I proceeded to offer a positive explication of the intrinsic canon model, including a survey of the considerable internal data of the canon suggestive of its nature and status as a divinely commissioned covenant document. Having seen that intrinsic canonicity is not an external imposition on the biblical writings but congruent with its internal testimony, I turned to the recognition of which writings were indeed canonical. Toward this end, three mutually reinforcing traits intrinsic to canonical books were identified as criteria of recognition: divine commission as prophetic or apostolic, consistency with past revelation, and self-authentication by divine purpose. The application of these three traits appears to confirm the common canonical core of thirty-nine OT books and twenty-seven NT books.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Now that the intrinsic canon model has been explained, we are in a better position to take up the issue of whether the intrinsic canon approach is preferable to the community canon approach, with an eye toward implications for the divide between the canonical and communitarian theological methods.")]), _vm._v(" "), _c('p', {
    staticClass: "line"
  }, [_c('img', {
    attrs: {
      "alt": "line",
      "src": "images/line.jpg"
    }
  })])]), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn1"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_47"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#fn1"
    }
  }, [_vm._v("1")]), _vm._v(". See the discussion in chapter 3.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn2"
    }
  }, [_vm._v("2")]), _vm._v(". Michael J. Kruger, "), _c('i', [_vm._v("The Question of Canon: Challenging the Status Quo in the New Testament Debate")]), _vm._v(" (Downers Grove, IL: IVP Academic, 2013), 7, 22. Kruger makes a strong case for the latter, that canon was a natural development in early Christianity. Cf. Michael J. Kruger, "), _c('i', [_vm._v("Canon Revisited: Establishing the Origins and Authority of the New Testament Books")]), _vm._v(" (Wheaton, IL: Crossway, 2012).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn3"
    }
  }, [_vm._v("3")]), _vm._v(". I posit here a distinctively theological treatment of canon, which makes no pretense of neutrality. Every investigation requires starting points and mine is the supposition that God has revealed himself to humans (most fully in Christ) in a way that is recognizable. This is a minimal belief of Christianity which does not require the "), _c('i', [_vm._v("a priori")]), _vm._v(" supposition of an overarching theological system and itself appears to be a fundamental supposition of the NT (thus, it methodologically and theologically coheres with the canonical theological method laid out in later chapters). Accordingly, I devote considerable attention here to the internal testimony of the Bible regarding the nature of its contents.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn4"
    }
  }, [_vm._v("4")]), _vm._v(". See n. 5 in chapter 1 above. See also Kruger’s delineation of three types of community-determined canon models (historical-critical, Roman Catholic, and canonical-critical model); see his "), _c('i', [_vm._v("Canon Revisited")]), _vm._v(", 30-57.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn5"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn5"
    }
  }, [_vm._v("5")]), _vm._v(". Such community forces external to the canon might be Christian (ecclesial) forces or, in some theories, primarily political forces. See, for example, David L. Dungan, "), _c('i', [_vm._v("Constantine’s Bible: Politics and the Making of the New Testament")]), _vm._v(" (Minneapolis: Fortress, 2007). Cf. Robert W. Funk, “The Once and Future New Testament,” in "), _c('i', [_vm._v("The Canon Debate")]), _vm._v(", ed. Lee Martin McDonald and James A. Sanders (Peabody, MA: Hendrickson, 2002); Harry Y. Gamble, "), _c('i', [_vm._v("The New Testament Canon: Its Making and Meaning")]), _vm._v(" (Philadelphia: Fortress, 1985), 11-13, 57-67. See, further, the discussion of various supposed forces including political, ecclesiastical, heretical, and (Greek) philosophical, among others in Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 17-20.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn6"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn6"
    }
  }, [_vm._v("6")]), _vm._v(". James A. Sanders, "), _c('i', [_vm._v("Canon and Community: A Guide to Canonical Criticism")]), _vm._v(" (Philadelphia: Fortress, 1984), 15. Sanders delineates this in the context of scholarly “devalu[ation]” of “the meaning of the word canon in order to apply to the Bible the developing tools of historical investigation” ("), _c('i', [_vm._v("Canon and Community")]), _vm._v(", 1).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn7"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn7"
    }
  }, [_vm._v("7")]), _vm._v(". James A. Sanders, “The Issue of Closure in the Canonical Process,” in "), _c('i', [_vm._v("Canon Debate")]), _vm._v(", 252, 262.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn8"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn8"
    }
  }, [_vm._v("8")]), _vm._v(". Paul McGlasson, "), _c('i', [_vm._v("Invitation to Dogmatic Theology: A Canonical Approach")]), _vm._v(" (Grand Rapids: Brazos, 2006), 54. Cf. John R. Franke’s view, “Scripture, Tradition, and Authority: Reconstructing the Evangelical Conception of "), _c('i', [_vm._v("Sola Scriptura")]), _vm._v(",” in "), _c('i', [_vm._v("Evangelicals & Scripture: Tradition, Authority, and Hermeneutics")]), _vm._v(", ed. Vincent Bacote, Laura C. Miguélez, and Dennis L. Okholm (Downers Grove, IL: InterVarsity, 2004), 200, that “canonical Scripture is, on the one hand, constitutive of the church, providing the primary narratives around which the life and faith of the Christian community is shaped and formed and, on the other hand, is itself derived from that community and its authority.” Thus, “Scripture and tradition” are “inseparably bound together through the work of the Spirit.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn9"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn9"
    }
  }, [_vm._v("9")]), _vm._v(". Charles J. Scalise, "), _c('i', [_vm._v("From Scripture to Theology: A Canonical Journey into Hermeneutics")]), _vm._v(" (Downers Grove, IL: InterVarsity, 1996), 45.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn10"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn10"
    }
  }, [_vm._v("10")]), _vm._v(". Anthony C. Thiselton, “Canon, Community, and Theological Construction,” in "), _c('i', [_vm._v("Canon and Biblical Interpretation,")]), _vm._v(" ed. Craig G. Bartholomew et al. (Grand Rapids: Zondervan, 2006), 13, comments, “the church did not ‘make’ the canon, but through its life and identity recognized the formative impact of divine revelation through the call of the ‘prophets and apostles as a whole.’ ” For Milton Fisher, “The Canon of the New Testament,” in "), _c('i', [_vm._v("The Origin of the Bible")]), _vm._v(", ed. Philip Wesley Comfort (Wheaton, IL: Tyndale, 1992), 77, further, “what is really meant by canonization — [is] recognition of the divinely authenticated word.” Cf. Brevard S. Childs, "), _c('i', [_vm._v("Biblical Theology in Crisis")]), _vm._v(" (Philadelphia: Westminster, 1970), 105; F. F. Bruce, "), _c('i', [_vm._v("The Books and the Parchments")]), _vm._v(" (Glasgow: HarperCollins, 1991), 86–104; Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 39.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn11"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn11"
    }
  }, [_vm._v("11")]), _vm._v(". Therefore, as Gerhard F. Hasel, “Divine Inspiration and the Canon of the Bible,” "), _c('i', [_vm._v("Journal of the Adventist Theological Society")]), _vm._v(" 5/1 (1994), 73, contends, the “canon developed at the very point when the biblical books were written under inspiration.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn12"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn12"
    }
  }, [_vm._v("12")]), _vm._v(". Kruger adopts a “historical approach to the canon,” leading him to an “ontological” definition, which “focuses on what the canon is in and of itself” ("), _c('i', [_vm._v("Question")]), _vm._v(", 40). Here, “the canonical books are what they are by virtue of the divine purpose for which they were given, and not by virtue of their use or acceptance by the community of faith” ("), _c('i', [_vm._v("Question,")]), _vm._v(" 39). Kruger uses the phrase “intrinsic canon,” however, to refer to something else and contrasts his use of this phrase with my own by framing his as not in reference to “the authority of the canon but” to “the "), _c('i', [_vm._v("historical development")]), _vm._v(" of the canon” ("), _c('i', [_vm._v("Question,")]), _vm._v(" 22 n. 27, emphasis his). See John C. Peckham, “The Canon and Biblical Authority: A Critical Comparison of Two Models of Canonicity,” "), _c('i', [_vm._v("TJ")]), _vm._v(" 28/2 (2007): 229-49. By intrinsic canon, however, I do not refer "), _c('i', [_vm._v("merely")]), _vm._v(" to the authority of the canon but to what the canon "), _c('i', [_vm._v("is intrinsically")]), _vm._v(". Canonical writings are intrinsically authoritative "), _c('i', [_vm._v("as")]), _vm._v(" intrinsically canonical and they are intrinsically canonical in virtue of what they are due to divine action.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_vm._v("Whereas by “intrinsic” I mean intrinsic "), _c('i', [_vm._v("to the canon")]), _vm._v(" "), _c('i', [_vm._v("itself")]), _vm._v(" in virtue of what it is, it seems that by “intrinsic” Kruger means intrinsic "), _c('i', [_vm._v("to Christianity")]), _vm._v(" in the sense of flowing “organically from within the early Christian religion itself” (Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 21), as opposed to extrinsically imposed upon Christianity “from the outside.” If this differentiation is correct, someone might adopt Kruger’s intrinsic canon and yet fall under what I refer to as the community canon model (though Kruger himself would not, given his ontological definition; see "), _c('i', [_vm._v("Question")]), _vm._v(", 44, 46). Whereas Kruger’s primary concern in "), _c('i', [_vm._v("The Question of Canon")]), _vm._v(" appears to be whether the concept of canon is a late development imposed upon Christianity (arguing that it is not), my concern focuses on whether canonicity is imposed upon books by humans (whether from inside or outside Christianity) or intrinsic to the canonical writings in virtue of divine action.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn13"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn13"
    }
  }, [_vm._v("13")]), _vm._v(". 2 Timothy 3:16 likely refers specifically to OT Scriptures, but the theological import of the verse may apply to later writings insofar as those writings are also genuinely “Scripture” in this sense (cf. 2 Pet 3:15-16).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn14"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn14"
    }
  }, [_vm._v("14")]), _vm._v(". The phrase “divine commission,” then, does not require commitment to a particular model of revelation-inspiration. For my view, in this regard, see chapter 8.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn15"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn15"
    }
  }, [_vm._v("15")]), _vm._v(". On "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("κανών")]), _vm._v(", see Bruce M. Metzger, "), _c('i', [_vm._v("The Canon of the New Testament: Its Origin, Development, and Significance")]), _vm._v(" (Oxford: Clarendon, 1987), 289-93; Eugene Ulrich, “The Notion and Definition of Canon,” in "), _c('i', [_vm._v("Canon Debate")]), _vm._v(", 21-35; and Hermann Beyer, “"), _c('span', {
    staticClass: "grk"
  }, [_vm._v("κανών")]), _vm._v(",” "), _c('i', [_vm._v("TDNT")]), _vm._v(" 3:596-602.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn16"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn16"
    }
  }, [_vm._v("16")]), _vm._v(". Such use in the sense of “rule” or “standard” also appears in early Christian writings of a "), _c('i', [_vm._v("canon")]), _vm._v(" of faith or "), _c('i', [_vm._v("canon")]), _vm._v(" of truth (Irenaeus, "), _c('i', [_vm._v("Against Heresies")]), _vm._v(" 1.9.4; Clement of Alexandria, "), _c('i', [_vm._v("Miscellanies")]), _vm._v(" 6.15; cf. Eusebius, "), _c('i', [_vm._v("Ecclesiastical History")]), _vm._v(" 6.25.3; see chapter 5). The term also appears elsewhere in the NT and LXX with a broader range of meaning beyond “rule” or “standard.” See 2 Cor 10:13, 15, 16; cf. LXX Judith 13:6; 4 Macc 7:21; Micah 7:4.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn17"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn17"
    }
  }, [_vm._v("17")]), _vm._v(". Cf. the redemptive-historical approach wherein Scripture is a “product of God’s revelatory activity in the history of redemption”; Herman N. Ridderbos, "), _c('i', [_vm._v("Redemptive History and the New Testament Scriptures,")]), _vm._v(" 2nd rev. ed. (Phillipsburg, NJ: Presbyterian and Reformed, 1988), ix. Here, “the apostles’ [Christ-commissioned] role in the history of redemption was unique and unrepeatable. . . . Christ established a formal authority structure” as “source and standard” ("), _c('i', [_vm._v("Redemptive History,")]), _vm._v(" 13). Christ thus bestowed authority on the canon, which is finally closed based “on the once-for-all significance of the New Testament history of redemption itself,” which required “a "), _c('i', [_vm._v("written form")]), _vm._v("” to “exist permanently” beyond the apostles ("), _c('i', [_vm._v("Redemptive History,")]), _vm._v(" 25). The NT canonical writings are thus inherently authoritative due to their location in redemption history.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn18"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn18"
    }
  }, [_vm._v("18")]), _vm._v(". Contra Sundberg’s influential but artificial distinction between “Scripture” and “canon” (see chapter 3). As Iain Provan, “Canons to the Left of Him: Brevard Childs, His Critics, and the Future of Old Testament Theology,” "), _c('i', [_vm._v("SJT")]), _vm._v(" 50/1 (1997): 10, notes, the very “idea of scripture” itself implies “the idea of limitation, of canon, even if it is not yet conceived that the limits have been reached.” As such, “the necessity of a strict demarcation between Scripture and canon largely disappears” (Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 20; cf. Kruger, "), _c('i', [_vm._v("Canon Revisited")]), _vm._v(", 35-38). Here, the crucial matter is not the term(s) used but the way biblical writers describe the nature and authority of biblical writings.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn19"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn19"
    }
  }, [_vm._v("19")]), _vm._v(". Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 24.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn20"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn20"
    }
  }, [_vm._v("20")]), _vm._v(". Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 61. See the compelling case in this regard in Andreas J. Köstenberger and Michael J. Kruger, "), _c('i', [_vm._v("The Heresy of Orthodoxy")]), _vm._v(" (Wheaton, IL: Crossway, 2010), 109-15. They conclude that “canon is inherent to and derives its function from the concept of covenant” such that canon is “the "), _c('i', [_vm._v("inevitable result of covenant")]), _vm._v("” ("), _c('i', [_vm._v("Heresy")]), _vm._v(", 112; emphasis theirs). For Kruger, then, “"), _c('i', [_vm._v("the canon is a treaty document")]), _vm._v("” ("), _c('i', [_vm._v("Question")]), _vm._v(", 61, emphasis his). Compare Meredith G. Kline’s view, "), _c('i', [_vm._v("The Structure of Biblical Authority")]), _vm._v(" (Grand Rapids: Eerdmans, 1972), 45-75, here 75, that “canon is inherent in covenant of the biblical type” and thus “canonicity is inherent in the very form and identity of Scripture as the Old Testament and the New Testament.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn21"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn21"
    }
  }, [_vm._v("21")]), _vm._v(". Köstenberger and Kruger, "), _c('i', [_vm._v("Heresy")]), _vm._v(", 111.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn22"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn22"
    }
  }, [_vm._v("22")]), _vm._v(". Köstenberger and Kruger, "), _c('i', [_vm._v("Heresy")]), _vm._v(", 113. Cf. Richard M. Davidson, “The Divine Covenant Lawsuit Motif in Biblical Perspective,” "), _c('i', [_vm._v("Journal of the Adventist Theological Society")]), _vm._v(" 21/1-2 (2010): 45-84.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn23"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn23"
    }
  }, [_vm._v("23")]), _vm._v(". Köstenberger and Kruger, "), _c('i', [_vm._v("Heresy")]), _vm._v(", 113. Cf. Kevin Vanhoozer’s view, "), _c('i', [_vm._v("The Drama of Doctrine: A Canonical-Linguistic Approach to Christian Theology")]), _vm._v(" (Louisville: Westminster John Knox, 2005), 134, cf. 137-39, of the “canon” as “a divinely initiated covenant document.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn24"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn24"
    }
  }, [_vm._v("24")]), _vm._v(". Köstenberger and Kruger, "), _c('i', [_vm._v("Heresy")]), _vm._v(", 113.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn25"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn25"
    }
  }, [_vm._v("25")]), _vm._v(". Cf. Kline’s view that the NT reflects the OT’s covenantal structure ("), _c('i', [_vm._v("Structure")]), _vm._v(", 68-75). Cf. also the typology of Christ as second Moses.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn26"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn26"
    }
  }, [_vm._v("26")]), _vm._v(". Regarding the claim that Christ did not explicitly commission the NT "), _c('i', [_vm._v("writing")]), _vm._v(", see below. Compare Adolf von Harnack’s comment, "), _c('i', [_vm._v("The Origin of the New Testament and the Most Important Consequences of the New Creation")]), _vm._v(" (New York: Macmillan, 1925), 13, that the “conception of the ‘New Covenant’ necessarily suggested the need of something of "), _c('i', [_vm._v("the nature of a document")]), _vm._v("; for what is a covenant without its document?” (emphasis his).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn27"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn27"
    }
  }, [_vm._v("27")]), _vm._v(". While the precise books to which Jesus refers are unidentified, as Craig A. Evans, “The Scriptures of Jesus and His Earliest Followers,” in "), _c('i', [_vm._v("Canon Debate")]), _vm._v(", 195, notes, Luke 24:44 manifests “awareness of additional authoritative writings” although “the contours of a third division of Scripture remain undefined”; cf. also Christopher Seitz, "), _c('i', [_vm._v("The Goodly Fellowship of the Prophets: The Achievement of Association in Canon Formation")]), _vm._v(" (Grand Rapids: Baker Academic, 2009), 97-100. Consider further Christ’s reference to “Daniel the prophet” in Matt 24:15. Note also that 2 Macc 15:9 records Maccabeus encouraging his men “from the law and the prophets.” Cf. 4QMMT"), _c('sup', [_vm._v("c")]), _vm._v(" 10; b. Baba Batra 13b; Sir 39:1; Philo, "), _c('i', [_vm._v("Contempl. Life")]), _vm._v(", 25. Additionally, Josephus, "), _c('i', [_vm._v("Against Apion")]), _vm._v(" 1.8, delineates (without naming each book) a three-part corpus as five books of Moses, thirteen of the Prophets (from the death of Moses to the time of Artaxerxes), and four containing “hymns to God and precepts for the conduct of human life” (translation following Josephus, "), _c('i', [_vm._v("The Life; Against Apion")]), _vm._v(", translated by H. St. J. Thackeray, Loeb Classical Library 186 [Cambridge, MA: Harvard University Press, 1926], 179). Kruger concludes, in this regard, “although it is disputed by some scholars, there are good reasons to think the threefold canonical structure of the Old Testament would have been established by the time of Jesus” ("), _c('i', [_vm._v("Question")]), _vm._v(", 49). Cf. Roger T. Beckwith, "), _c('i', [_vm._v("The Old Testament Canon of the New Testament Church")]), _vm._v(" (Grand Rapids: Eerdmans, 1986), 110-80; Stephen B. Chapman, “The Old Testament Canon and Its Authority for the Christian Church,” "), _c('i', [_vm._v("ExAud")]), _vm._v(" 19 (2003): 125-48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn28"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn28"
    }
  }, [_vm._v("28")]), _vm._v(". This refers to Zechariah, the son of Jehoiada, as specified by the description of Zechariah’s death (2 Chr 24:20-21). The parallel reference in Matt 23:35 introduces some confusion, also referring to this manner of death but identifying him as “the son of Berechiah.” This patronymic is that of the prophet Zechariah (Zech 1:1), not the same figure whose death is recounted in Chronicles; it is likely a mistaken gloss (cf. Targum Lamentations 2.20). Evans takes this reference as “probably meant to sum up Israel’s history, not Israel’s sacred scripture” (“Scriptures,” 190). However, given that Israel’s history continued long after the death of Zechariah, as did the martyrdom of God’s messengers (e.g., John the Baptist), this seems to me unlikely.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn29"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn29"
    }
  }, [_vm._v("29")]), _vm._v(". “According to the Synoptic Gospels, Jesus quotes or alludes to twenty-three of the thirty-nine books of the Hebrew Bible.” He “alludes to or quotes all five books of Moses, the three major prophets (Isaiah, Jeremiah, and Ezekiel), eight of the twelve minor prophets, and five of the Writings” (Evans, “Scriptures,” 185). Cf. R. T. France, "), _c('i', [_vm._v("Jesus and the Old Testament")]), _vm._v(" (Grand Rapids: Baker, 1982), 259-63. While the presence or absence of a quote or allusion may not itself indicate how a source was viewed (numerous sources are alluded to without connoting authority or inspiration; cf. Acts 17:28; Tit 1:12), it is notable that each section of the Hebrew Bible is very well represented by quotes and allusions throughout the NT (frequently quoted as authoritative Scripture). On Roger Nicole’s count, “New Testament Use of the Old Testament,” in "), _c('i', [_vm._v("Revelation and the Bible")]), _vm._v(", ed. Carl F. H. Henry (Grand Rapids: Baker, 1958), 138, of “specific quotations and direct allusions,” “278 different Old Testament verses are cited in the New Testament: 94 from the Pentateuch, 99 from the Prophets, and 85 from the Writings. Out of the 22 books in the Hebrew reckoning of the Canon only six (Judges-Ruth, Song of Solomon, Ecclesiastes, Esther, Ezra-Nehemiah, Chronicles) are not explicitly referred to” while there are “passages reminiscent of all Old Testament books without exception.” Craig Blomberg, "), _c('i', [_vm._v("Can We Still Believe the Bible?")]), _vm._v(" (Grand Rapids: Brazos, 2014), 49, finds it “particularly telling” that the NT “explicitly quotes from a broad-section of Old Testament documents but never quotes from the OT Apocrypha,” though “one may speculate about numerous possible references to both apocryphal and pseudepigraphic texts.” For a list of possible references to the OT Apocrypha see McDonald, "), _c('i', [_vm._v("Biblical Canon")]), _vm._v(", 452-64.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn30"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn30"
    }
  }, [_vm._v("30")]), _vm._v(". Thus, Childs believes, “From the evidence of the New Testament it seems clear that Jesus and the early Christians identified with the scriptures of Pharisaic Judaism” ("), _c('i', [_vm._v("Biblical Theology")]), _vm._v(", 26). Cf. Peter Balla, “Evidence for an Early Christian Canon (Second and Third Century),” in "), _c('i', [_vm._v("Canon Debate")]), _vm._v(", 372-85; Beckwith, "), _c('i', [_vm._v("Old Testament Canon,")]), _vm._v(" 165-66; F. F. Bruce, "), _c('i', [_vm._v("The Canon of Scripture")]), _vm._v(" (Downers Grove, IL: InterVarsity, 1988), 255. Whereas McDonald raises questions about this view, he does recognize that “many OT citations and allusions in the Gospels attest to the authoritative status of the OT in the life and ministry of the early Christian communities” ("), _c('i', [_vm._v("Formation")]), _vm._v(", 9). Cf. McDonald, "), _c('i', [_vm._v("Biblical Canon")]), _vm._v(", 31-32, 426. Consider, for instance, the way that Christ referred to and used OT Scripture as authoritative (e.g., Matt 4:4-10; 5:17-19; 11:13-14; 12:3-6, 39-42; 15:4-6; 19:4-9; 21:42-44; 22:29-32, 37-40, 43-45; 24:9-21; 26:31-32, 54-56; Mark 1:44; 7:6; 10:3; 12:26; Luke 8:21; 11:28; 16:16-17; 24:27, 44-45; John 2:22; 5:39; 10:34-35; 17:12, 17). See also the way the rest of the NT testifies to OT writings as authoritative Scripture (e.g., Acts 17:2; 18:28; Rom 1:2; 4:3; 9:17; 10:11; 11:2; 1 Cor 15:3, 4; Gal 3:8; 2 Tim 3:16; 2 Pet 1:20-21).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn31"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn31"
    }
  }, [_vm._v("31")]), _vm._v(". James Barr, "), _c('i', [_vm._v("Holy Scripture: Canon, Authority and Criticism")]), _vm._v(" (Philadelphia: Westminster, 1983), 12. McDonald similarly notes that Jesus did not write or command anything to be written except in “Revelation, where Jesus commands the angels of the churches to put his message in written form (Rev 2:1–3:14)” ("), _c('i', [_vm._v("Formation")]), _vm._v(", 7; cf. McDonald, "), _c('i', [_vm._v("Biblical Canon")]), _vm._v(", 426; Gamble, "), _c('i', [_vm._v("New Testament Canon,")]), _vm._v(" 57).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn32"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn32"
    }
  }, [_vm._v("32")]), _vm._v(". Mark Alan Powell, "), _c('i', [_vm._v("Introducing the New Testament: A Historical, Literary, and Theological Survey")]), _vm._v(" (Grand Rapids: Baker Academic, 2009), 50, goes further, claiming that the “authors of our New Testament did not know that they were writing scripture.” However, one wonders just how Powell knows what NT authors knew and did not know. Further, McDonald claims that Paul “apparently was unaware of the divinely inspired status of his own advice ([1 Cor] 7:12, 25)” ("), _c('i', [_vm._v("Biblical Canon")]), _vm._v(", 32). But in the very same chapter Paul explicitly claims to have the Spirit of God (1 Cor 7:40; cf. Rom 15:15; see the further discussion below) and many believe Paul’s statement in verse 12 is simply a way of indicating that he is not quoting Jesus. Kruger, moreover, notes NT writers “expressly understood their writings to be apostolic in nature” and, as such, “functionally” the same as “Scripture” ("), _c('i', [_vm._v("Question")]), _vm._v(", 153). Cf. N. T. Wright, "), _c('i', [_vm._v("Scripture and the Authority of God")]), _vm._v(" (San Francisco: HarperCollins, 2011), 51.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn33"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn33"
    }
  }, [_vm._v("33")]), _vm._v(". Notably, verse 43 adds, “Of Him all the prophets bear witness.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn34"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn34"
    }
  }, [_vm._v("34")]), _vm._v(". “Given the authoritative role of the apostles in early Christianity, and the manner in which they were commissioned to speak for Christ, an apostolic writing would bear the highest possible authority. Indeed, it would bear Christ’s authority” (Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 153).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn35"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn35"
    }
  }, [_vm._v("35")]), _vm._v(". In Kruger’s view, “the mission of the apostles” made “the resulting collection of authoritative books — a virtual inevitability” ("), _c('i', [_vm._v("Question")]), _vm._v(", 76). Kruger includes in his study an extensive, compelling case that “written records and oral/eyewitness testimony were not in opposition to one another; on the contrary, written accounts were simply a way to make oral/eyewitness testimony permanently accessible” ("), _c('i', [_vm._v("Question")]), _vm._v(", 72).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn36"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn36"
    }
  }, [_vm._v("36")]), _vm._v(". As George Lindbeck, "), _c('i', [_vm._v("The Nature of Doctrine: Religion and Theology in a Postliberal Age")]), _vm._v(", 1st ed. (Philadelphia: Westminster, 1984), 116, notes, “purely customary religions and cultures readily dissolve under the pressure of historical, social and linguistic change,” such that “canonical texts are a condition, not only for the survival of a religion but for the very possibility of normative theological description.” Likewise, Kruger notes that the “need to textualize the oral tradition of founding members (or eyewitnesses) is a pattern that has also been recognized by scholars who study social and cultural memory theory” and “typically occurs within forty years of the community’s founding” ("), _c('i', [_vm._v("Question")]), _vm._v(", 72, 73).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn37"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn37"
    }
  }, [_vm._v("37")]), _vm._v(". Here and elsewhere the apostolic testimony is framed as “witness” ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("μάρτυς")]), _vm._v("). See Luke 24:48-49; Acts 1:8, 21-22; 2:32; 3:15; 5:32; 10:39-41; 13:31; 22:15; 23:11; 26:16; 1 Pet 5:1; cf. Luke 1:2; John 15:26-27; 19:35; 20:30; 21:24; Acts 4:20; 1 Cor 15:15; Heb 12:1; 2 Pet 1:16; 1 John 1:1-4.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn38"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn38"
    }
  }, [_vm._v("38")]), _vm._v(". Regarding the “word of God,” see also Luke 3:2; 5:1; 8:11, 21; 11:28; Acts 4:31; 6:2, 7; 8:14; 11:1; 13:5, 7, 46; 17:13; 18:11; 1 Cor 14:36; 2 Cor 2:17; Eph 6:17; Phil 1:14; Col 1:25; 1 Thess 2:13; 1 Tim 4:5; 2 Tim 2:9; Heb 13:7; Rev 1:2. Notice also the instances where “word of God” explicitly refers to “Scripture” (Matt 15:6; Mark 7:13; Rom 9:6; Heb 4:12).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn39"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn39"
    }
  }, [_vm._v("39")]), _vm._v(". See the discussion of tradition, apostolic and otherwise, in chapters 5-6.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn40"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn40"
    }
  }, [_vm._v("40")]), _vm._v(". Whether this is a quote from Luke’s gospel is a matter of much dispute. In this regard, Köstenberger and Kruger see Luke 10:7 as the “clear and obvious source for this citation.” Given that this phrase occurs “"), _c('i', [_vm._v("only")]), _vm._v(" in these two texts,” why “insist upon hypothetical and conjectural sources” (such as “Q or an apocryphal gospel)?” ("), _c('i', [_vm._v("Heresy")]), _vm._v(", 130). See the discussion of scholarly opinions in Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 201-2.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn41"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn41"
    }
  }, [_vm._v("41")]), _vm._v(". This evidence, however, is sometimes dismissed by dating 2 Peter as late as AD 180 (see McDonald, "), _c('i', [_vm._v("Formation")]), _vm._v(", 9). However, if 2 Peter is genuinely Petrine, as I believe it is, this is strong evidence indeed. See the brief discussion of the dating of NT writings further below.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn42"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn42"
    }
  }, [_vm._v("42")]), _vm._v(". Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 154.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn43"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn43"
    }
  }, [_vm._v("43")]), _vm._v(". Further, the NT repeatedly appeals to OT writings as authoritative (Rom 4:3), including appeals to “Scripture” and “it is written” recurrent in the words of Jesus (Matt 4:4-10; 11:10; 26:24; Mark 12:10; Luke 4:21; 10:26; John 7:42; 10:35, et al.).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn44"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn44"
    }
  }, [_vm._v("44")]), _vm._v(". Of Nehemiah 8, McDonald comments, “the notion of scripture was clearly present in Judaism” ("), _c('i', [_vm._v("Biblical Canon")]), _vm._v(", 30).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn45"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn45"
    }
  }, [_vm._v("45")]), _vm._v(". See the instances regarding “the hand of the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v("” in Ezek 1:3; 3:22; 37:1; 40:1, and “the word of the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(" came” or something similar in Jer 1:11, 13; Hos 1:1; Joel 1:1; Jonah 1:1; Mic 1:1, et al. Note also “hear the word of the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v("” or something similar in Amos 3:1; Jer 2:4; Ezek 6:3; Hos 4:1; cf. Jer 1:9; Ezek 3:4. “Thus says the L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v("” appears in many instances (e.g., Amos 1:3; Obad 1; Hag 1:7; Zech 12:1; Isa 7:7).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn46"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn46"
    }
  }, [_vm._v("46")]), _vm._v(". Hasel, “Divine Inspiration,” 78.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn47"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn47"
    }
  }, [_vm._v("47")]), _vm._v(". Here, a canonical “trait” is something intrinsic to the nature of “canonical” writing. A divinely commissioned prophetic and/or apostolic book will, by nature, be consistent with past revelation and self-authenticating.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn48"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn48"
    }
  }, [_vm._v("48")]), _vm._v(". Numerous similar references to the “prophets” and “apostles” appear elsewhere in early Christian literature. See Denis Farkasfalvy, “ ‘Prophets and Apostles’: The Conjunction of the Two Terms before Irenaeus,” in "), _c('i', [_vm._v("Texts and Testaments")]), _vm._v(", ed. Eugene March (San Antonio: Trinity University Press, 1980), 109-34.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn49"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn49"
    }
  }, [_vm._v("49")]), _vm._v(". The Gospels of Mark and Luke, for instance, are traditionally attributed to their namesakes and early Christian evidence suggests that both had close apostolic associations. Luke utilized the (apostolic) testimony of firsthand ministers and witnesses (Luke 1:1-5; cf. 24:44, 48; Acts 6:4; 26:16) and is identified as a close associate of Paul (Col 4:14; 2 Tim 4:11; Phlm 24; cf. Acts). Further, according to considerable patristic evidence, Mark recorded Peter’s account (e.g., Papias cited in Eusebius, "), _c('i', [_vm._v("Ecclesiastical History")]), _vm._v(" 3.39.14-15; Justin, "), _c('i', [_vm._v("Dialogue with Trypho")]), _vm._v(" 106; Irenaeus, "), _c('i', [_vm._v("Against Heresies")]), _vm._v(", 3.10.5; Tertullian, "), _c('i', [_vm._v("Against Marcion")]), _vm._v(", 4.5; Eusebius, "), _c('i', [_vm._v("Ecclesiastical History")]), _vm._v(" 2.15.1-2; 6.14.6 [attributed to Clement of Alexandria]). The NT manifests Mark’s apostolic associations (1 Pet 5:13; Acts 12:12-17, 25; 15:37, 39; 2 Tim 4:11) and the contents of Mark point to Peter as primary witness (cf. Acts 10:34-43); Richard Bauckham, "), _c('i', [_vm._v("Jesus and the Eyewitnesses: The Gospels as Eyewitness Testimony")]), _vm._v(" (Grand Rapids: Eerdmans, 2006), 125-27, 205-10. Indeed, some suggest he was the young man who fled in Mark 14:51-52. If the traditional view and/or the internal testimony of these gospels is accurate (see page 42), then both are genuinely apostolic.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn50"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn50"
    }
  }, [_vm._v("50")]), _vm._v(". Not merely any prophecy is “canonical” but only that which is covenantal witness to God’s redemptive events/revelation (requiring genuine prophets in the covenantal history of Israel for the OT and apostolic witness to the new covenant for the NT).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn51"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn51"
    }
  }, [_vm._v("51")]), _vm._v(". "), _c('i', [_vm._v("Against Apion")]), _vm._v(" 1.7, 8 (LCL 186:179, 181). Josephus refers to twenty-two books, which many scholars believe are the same as the twenty-four books of the Hebrew Bible today but numbered twenty-two to correspond to the Hebrew alphabet. Cf. Beckwith, "), _c('i', [_vm._v("Old Testament Canon")]), _vm._v(", 235-73.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn52"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn52"
    }
  }, [_vm._v("52")]), _vm._v(". L. Stephen Cook, "), _c('i', [_vm._v("On the Question of the “Cessation of Prophecy” in Ancient Judaism")]), _vm._v(" (Tübingen: Mohr Siebeck, 2011), 136, cf. 131-32, concludes that, whereas Josephus notes some prophetic phenomena in later ages and nowhere excludes the possibility that true prophets could reappear, he “does not seem to believe that true "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("προφήται")]), _vm._v(" (prophets) and "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("προφητεία")]), _vm._v(" (prophecy) continued beyond the Persian period.” He views Josephus as “assuming some beliefs — probably common among Jews of his day — about the relationship of the ancient prophets to the collection of Jewish Scriptures, and about a fundamental change in the prophetic tradition around the time of Artaxerxes” (133).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn53"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn53"
    }
  }, [_vm._v("53")]), _vm._v(". As Cook puts it, “Second Temple Jews did, on the whole, tend to believe that prophecy had ceased in the Persian period” ("), _c('i', [_vm._v("On the Question")]), _vm._v(", 192, cf. 47-177). This is among the reasons Protestants cite for excluding the OT Apocrypha from the canon (see below).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn54"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn54"
    }
  }, [_vm._v("54")]), _vm._v(". Cf. 1 Macc. 4:46; 14:41.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn55"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn55"
    }
  }, [_vm._v("55")]), _vm._v(". See the earlier discussion of “witness” in the NT.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn56"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn56"
    }
  }, [_vm._v("56")]), _vm._v(". See the discussion of apostolicity further below.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn57"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn57"
    }
  }, [_vm._v("57")]), _vm._v(". F. F. Bruce, “The Bible,” in "), _c('i', [_vm._v("Origin of the Bible")]), _vm._v(", 9. However, McDonald questions the contemporary use of the criteria of apostolicity and antiquity because of the questions of authorship and dating of some NT books according to the historical-critical method ("), _c('i', [_vm._v("Formation")]), _vm._v(", 230). Of course, the extant data regarding authorship may be interpreted against prophetic/apostolic authorship (and close contemporary association), and often has been. However, other interpretations of the "), _c('i', [_vm._v("extant")]), _vm._v(" data support faith in the propheticity and apostolicity of the biblical books. The scope of this work does not allow a satisfactory examination of the technical issues involved in such discussions. However, I do believe that (given minimal Christian beliefs) the weight of the "), _c('i', [_vm._v("extant")]), _vm._v(" evidence supports the view that the thirty-nine OT books and twenty-seven NT books are genuinely prophetic and apostolic. In my view, the purported counterevidence is inconclusive at best and rests heavily on speculative reconstructions that have little or no basis in extant historical data and often rely on presuppositions that contradict (a priori) the internal data of the writings under investigation. Because so many theoretical reconstructions are possible, I prefer to rely on the extant evidence and thus give little weight to speculative reconstructions. Compare C. Stephen Evans’s incisive explanation, “Canonicity, Apostolicity, and Biblical Authority: Some Kierkegaardian Reflections,” in "), _c('i', [_vm._v("Canon and Biblical Interpretation")]), _vm._v(", 159, cf. 159-64, of why he is “skeptical about some of the skepticism” observed “among biblical scholars about the historical authorship of many New Testament books.” See also the brief discussion in chapter 8. Given that one could claim that "), _c('i', [_vm._v("any")]), _vm._v(" book is not genuinely apostolic (without any extant evidence), it seems reasonable to suppose that a plausibly ancient book’s claim of apostolicity (for instance) counts as evidence in favor of apostolicity unless there is sufficient "), _c('i', [_vm._v("extant")]), _vm._v(" counterevidence. Many NT books make an explicit (or implicit) claim to apostolicity and many of these are bolstered by the extant evidence from the earliest post-apostolic Christians (which should not be dismissed lightly, given their historical proximity). Regarding this sometimes murky yet quite helpful evidence, see chapter 3.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_vm._v("One might ask, however, why accept the apostolic claims of the NT books but not others? Beyond what I take to be the self-authenticating witness of such books (see the third criterion), in my view (and that of the vast majority of Christians past and present) there is sufficient extant evidence to discount the claims to apostolicity made by some extracanonical books but there is insufficient extant evidence against the apostolicity of any of the twenty-seven NT books (not to mention the ways in which extracanonical books fail the other two criteria). For a case in favor of NT apostolicity, see D. A. Carson and Douglas J. Moo, "), _c('i', [_vm._v("An Introduction to the New Testament")]), _vm._v(", 2nd ed. (Grand Rapids: Zondervan, 2005). Regarding OT propheticity, see Gleason Archer, "), _c('i', [_vm._v("A Survey of Old Testament Introduction")]), _vm._v(", 3rd ed. (Chicago: Moody, 1998); Andrew E. Hill and John H. Walton, "), _c('i', [_vm._v("A Survey of the Old Testament")]), _vm._v(", 3rd ed. (Grand Rapids: Zondervan, 2009). Cf. the discussion of NT apostolicity in Kruger, "), _c('i', [_vm._v("Canon Revisited")]), _vm._v(", 174-94.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn58"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn58"
    }
  }, [_vm._v("58")]), _vm._v(". The Muratorian Fragment claims that "), _c('i', [_vm._v("Shepherd")]), _vm._v(" was written “very recently, in our times” during the time of Pius, bishop of Rome (ca. 140-154). It thus “cannot be read publicly to the people in church either among the prophets, whose number is complete, or among the apostles, for it is after their time” (lines 74, 78-80, in Metzger, "), _c('i', [_vm._v("The Canon,")]), _vm._v(" 307).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn59"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn59"
    }
  }, [_vm._v("59")]), _vm._v(". For example, "), _c('i', [_vm._v("1 Clement")]), _vm._v(" (ca. 96) comments, “Christ therefore was sent forth by God, and the apostles by Christ” and refers to the apostles as “the greatest and most righteous pillars [of the Church]” (42, 5; "), _c('i', [_vm._v("ANF")]), _vm._v(" 1:16, 6). Cf. Ignatius, "), _c('i', [_vm._v("To the Magnesians")]), _vm._v(" 7; Polycarp, "), _c('i', [_vm._v("To the Philippians")]), _vm._v(" 6; Irenaeus, "), _c('i', [_vm._v("Against Heresies,")]), _vm._v(" 3.1.1. "), _c('i', [_vm._v("1 Clement")]), _vm._v(", Ignatius, Justin Martyr, and Polycarp also clearly distinguish their authority from the apostles ("), _c('i', [_vm._v("1 Clement")]), _vm._v(" 5; Ignatius, "), _c('i', [_vm._v("To the Romans")]), _vm._v(" 4; Justin Martyr, "), _c('i', [_vm._v("First Apology")]), _vm._v(" 39; Polycarp, "), _c('i', [_vm._v("To the Philippians")]), _vm._v(" 3).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn60"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn60"
    }
  }, [_vm._v("60")]), _vm._v(". Bruce, "), _c('i', [_vm._v("Canon")]), _vm._v(", 256-58. Moreover, the arguments over authorship in the 2nd and 3rd centuries show “how important some degree of apostolic authorization seemed to be for the books which the church accepted as uniquely authoritative” ("), _c('i', [_vm._v("Canon")]), _vm._v(", 258). Thus, “if a writing was believed to have been produced by an apostle, it was eventually accepted as sacred Scripture and included in the New Testament canon”; Lee Martin McDonald, “Identifying Scripture and Canon in the Early Church: The Criteria Question,” in "), _c('i', [_vm._v("Canon Debate")]), _vm._v(", 424. Cf. Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 77.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn61"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn61"
    }
  }, [_vm._v("61")]), _vm._v(". This is similar to what some have called the test of orthodoxy but, here, the criterion is not conformity to tradition generally or the community’s belief system but agreement with past revelation such that canonical writings might reform and change tradition (see chapter 3).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn62"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn62"
    }
  }, [_vm._v("62")]), _vm._v(". The prophets after Moses could be tested by the standard of the Law. The NT, then, could be tested and recognized in light of the OT as the fulfillment thereof.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn63"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn63"
    }
  }, [_vm._v("63")]), _vm._v(". In the NT a prophet was judged by a confession of Jesus Christ (1 John 4:2; 1 Cor 12:3, 10). The "), _c('i', [_vm._v("Didache")]), _vm._v(" 11, 16 instructs its audience to test the prophets. Consider also the case of Serapion at Rhossus, who originally allowed usage of the so-called "), _c('i', [_vm._v("Gospel of Peter")]), _vm._v(" but later rejected it altogether because it implied Docetism (Eusebius, "), _c('i', [_vm._v("Ecclesiastical History")]), _vm._v(" 6.12.1-6).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn64"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn64"
    }
  }, [_vm._v("64")]), _vm._v(". While a skeptic might “find” contradiction in nearly anything and, likewise, a dogmatist might “find” congruency in anything (via special pleading), one might approach texts via a charitable, ethical reading (see chapter 9) in an attempt to avoid doing either and letting texts speak for themselves as much as possible while recognizing there may be unity without uniformity (cf. the distinction between "), _c('i', [_vm._v("ipse")]), _vm._v(" and "), _c('i', [_vm._v("idem")]), _vm._v(" identity in chapter 8). On this approach, many extracanonical writings demonstrably exclude themselves. For instance, “Simon Peter said to them: ‘Let Mary [Magdalene] leave us, for women are not worthy of life.’ Jesus said, ‘I myself shall lead her in order to make her male, so that she too may become a living spirit resembling you males. For every woman who will make herself male will enter the kingdom” ("), _c('i', [_vm._v("Gospel of Thomas")]), _vm._v(", 114).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn65"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn65"
    }
  }, [_vm._v("65")]), _vm._v(". “All of the ancient church fathers believed that their canon of scriptures was inspired” (McDonald, "), _c('i', [_vm._v("Formation")]), _vm._v(", 239). For an extensive case for the self-authentication of the biblical canon see Kruger, "), _c('i', [_vm._v("Canon Revisited")]), _vm._v(", 88-122, 125-59.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn66"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn66"
    }
  }, [_vm._v("66")]), _vm._v(". Some examples of explicitly prophetic books are the book of statutes (1 Sam 10:25), the book of Nathan the Prophet and of Gad the Seer (1 Chr 29:29), and the book of Shemaiah the prophet and of Iddo the Seer (2 Chr 12:15), among many others. Such prophetic books, and apparently some apostolic writings (1 Cor 5:9; 2 Cor 2:4; cf. Col 4:16), were not preserved through the ages for the present community.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn67"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn67"
    }
  }, [_vm._v("67")]), _vm._v(". See the earlier discussion, see page 36, note 58.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn68"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn68"
    }
  }, [_vm._v("68")]), _vm._v(". Accordingly, no post-apostolic inspired prophet would be “canonical”; see below.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn69"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn69"
    }
  }, [_vm._v("69")]), _vm._v(". For an introduction to the reliability of the OT and NT see Wayne Grudem, C. John Collins, and Thomas R. Schreiner, eds., "), _c('i', [_vm._v("Understanding Scripture: An Overview of the Bible’s Origin, Reliability, and Meaning")]), _vm._v(" (Wheaton, IL: Crossway, 2012).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn70"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn70"
    }
  }, [_vm._v("70")]), _vm._v(". McDonald sees usage as “probably the primary key to understanding the preservation and canonization of the books that make up our current NT” ("), _c('i', [_vm._v("Formation")]), _vm._v(", 249). Sanders, further, emphasizes adaptability and survivability: “Relevance or adaptability has always been the primary trait of a canon, early and late. When one speaks of a canon, in fact, one has to ask which canon of which community is meant, whether in antiquity or today” (“Issue of Closure,” 259).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn71"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn71"
    }
  }, [_vm._v("71")]), _vm._v(". Indeed, even some who do not endorse a community-determined canon set forth community reception as an attribute of canonicity. See Kruger, "), _c('i', [_vm._v("Canon Revisited")]), _vm._v(", 103-8, 120-21. For Kruger, community consensus does not make the canon canonical but is itself directed by God’s activity.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn72"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn72"
    }
  }, [_vm._v("72")]), _vm._v(". As Bruce states, “it is easy to conclude that in reaching a conclusion on the limits of the canon they [Christians] were directed by a wisdom higher than their own” ("), _c('i', [_vm._v("Canon")]), _vm._v(", 282).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn73"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn73"
    }
  }, [_vm._v("73")]), _vm._v(". Why God did not guide each self-identifying Christian to recognize the same canon is a question for a different study, relating as it does to models of God’s providence, etc.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn74"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn74"
    }
  }, [_vm._v("74")]), _vm._v(". Whereas any genuinely canonical book would be covenantally prophetic and/or apostolic, consistent with past revelation, and self-authenticating, a writing could (theoretically) be canonical without being recognized as such by the community.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn75"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn75"
    }
  }, [_vm._v("75")]), _vm._v(". For a full tabulation see Norman L. Geisler and William E. Nix, "), _c('i', [_vm._v("A General Introduction to the Bible: Revised and Expanded")]), _vm._v(" (Chicago: Moody, 1986), 419-33.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn76"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn76"
    }
  }, [_vm._v("76")]), _vm._v(". Recall the crucial distinction between the intrinsic canon itself and what is believed to be the content thereof. The validity of the former does not hinge upon the latter and, thus, one might disagree with my conclusions regarding canon recognition but still adopt the intrinsic canon model of canonicity.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn77"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn77"
    }
  }, [_vm._v("77")]), _vm._v(". For a more detailed treatment, see Kruger’s extensive case for the NT canon in "), _c('i', [_vm._v("Canon Revisited")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn78"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn78"
    }
  }, [_vm._v("78")]), _vm._v(". This minimal Christian belief is itself ruled by the canonical data; see page 17, note 3. Here, confident recognition refers to that which is discernible, demonstrable, and defensible but need not be persuasive to all nor assuage every doubt (see chapter 9).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn79"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn79"
    }
  }, [_vm._v("79")]), _vm._v(". Cf. Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 147-48.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn80"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn80"
    }
  }, [_vm._v("80")]), _vm._v(". Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 131. For sustained argument that the gospels are substantially eyewitness testimony about Jesus, and that the eyewitnesses themselves would have remained through their lifetimes as accessible sources, see Bauckham, "), _c('i', [_vm._v("Jesus and the Eyewitnesses")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn81"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn81"
    }
  }, [_vm._v("81")]), _vm._v(". Cf. Kruger, "), _c('i', [_vm._v("Question")]), _vm._v(", 151.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn82"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn82"
    }
  }, [_vm._v("82")]), _vm._v(". In this brief treatment I cannot detail all of my reasons for these convictions or enter into the complex debates regarding the authorship and dating of particular books (e.g., claims that some books such as 2 Peter are pseudepigraphal) but I would recommend, in this regard, the careful and excellent work of Carson and Moo, "), _c('i', [_vm._v("Introduction to the New Testament")]), _vm._v(". Cf. Kruger, "), _c('i', [_vm._v("Canon Revisited")]), _vm._v(", 174-94.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn83"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn83"
    }
  }, [_vm._v("83")]), _vm._v(". For instance, the aforementioned "), _c('i', [_vm._v("Gospel of Thomas")]), _vm._v(" (see page 38, note 64), which also appears to have been written too late to be genuinely apostolic. See Nicholas Perrin, “Thomas: The Fifth Gospel?” "), _c('i', [_vm._v("JETS")]), _vm._v(" 49/1 (2006): 67-80.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn84"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn84"
    }
  }, [_vm._v("84")]), _vm._v(". Josephus, "), _c('i', [_vm._v("Against Apion")]), _vm._v(" 1.8.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn85"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn85"
    }
  }, [_vm._v("85")]), _vm._v(". Blomberg, "), _c('i', [_vm._v("Can We Still Believe")]), _vm._v(", 49.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn86"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn86"
    }
  }, [_vm._v("86")]), _vm._v(". Blomberg, "), _c('i', [_vm._v("Can We Still Believe")]), _vm._v(", 51 (emphasis his). Cf. Bruce, "), _c('i', [_vm._v("Canon")]), _vm._v(", 44-50.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn87"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn87"
    }
  }, [_vm._v("87")]), _vm._v(". Cf. Fisher’s claim that “the method of determination is not one of selection from a number of possible candidates (there "), _c('i', [_vm._v("are no")]), _vm._v(" other candidates, in actuality) but one of reception of authentic material and its consequent recognition by an ever-widening circle as the facts of its origin become known” (“Canon,” 75).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn88"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn88"
    }
  }, [_vm._v("88")]), _vm._v(". Note, however, that this approach to canon specifically and canonical theology broadly does not require adoption of a particular text form of Scripture but leaves that open to the best results of ongoing examination via textual criticism (see also chapter 8). “It is the book, and not the textual form of the book, that is canonical” (Ulrich, “Notion,” 30). In this regard, as noted by Evans, “evidence suggests that Jesus was not wedded to a particular text, but appealed to words, phrases and sometimes whole passages” in “an ad hoc, experiential fashion” (“Scriptures,” 195). What about, however, potentially authentic Jesus traditions and sayings sources? According to this view, authentic Jesus sayings may appear in extracanonical writings and this is not problematic. However, it is extremely difficult to identify with confidence (let alone conclusively) extracanonical Jesus sayings (other than those very close in content to those within the canon already). Consider the wide array of mutually exclusive perspectives in historical Jesus studies and in source and form criticism regarding extracanonical and canonical Gospels. While such studies can be of great interest and I think the best results of textual criticism should be taken very seriously, I do take the results of higher criticism to be generally unreliable as speculative and constantly shifting. In any case, the results of such studies are not determinative regarding this model of canonicity.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn89"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn89"
    }
  }, [_vm._v("89")]), _vm._v(". Whereas a canonical book need not be recognized in order to be canonical, it is plausible to believe that all canonical books have finally been recognized by most Christians.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn90"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn90"
    }
  }, [_vm._v("90")]), _vm._v(". Conversely, if doctrinal or interpretive authority is believed to be infused via such roles, then on what basis would one exclude allowing interpretive authority to the extracanonical documents of the Qumran community, for instance?")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn91"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn91"
    }
  }, [_vm._v("91")]), _vm._v(". “The closed nature of the canon thus rests ultimately on the once-for-all significance of the New Testament history of redemption itself, as that history is presented by the apostolic witness” (Ridderbos, "), _c('i', [_vm._v("Redemptive History,")]), _vm._v(" 25).")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-81d967f4","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/chapter002.vue
// module id = 78
// module chunks = 0